import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Sparkles,
    Download,
    Image as ImageIcon,
    Check,
    Copy,
    RefreshCw,
    Wand2,
    Share2,
    Type,
    Zap,
    X,
    Play,
    Pause,
    Video as VideoIcon,
    Music,
    Send,
    RotateCcw,
    FileText,
    Volume2,
    ExternalLink,
    Clock,
    Calendar,
    MapPin,
    Heart
} from 'lucide-react';
import API_BASE_URL from '../config';
import { useInviteAuth } from '../pages/Invite/InviteAuthContext';
import { openCashfreeCheckout } from '../utils/cashfree';

// --- Types ---
export type GenAiMode = 'text' | 'image' | 'video' | 'song';

export interface ChatMessage {
    id: string;
    role: 'user' | 'assistant';
    mode: GenAiMode;
    prompt: string;
    originalPrompt?: string;
    optimizedPrompt?: string;
    enhancementNotes?: string[];
    timestamp: Date;
    // Generated Payloads:
    content?: string;        // Formatted Text
    mediaUrl?: string;       // Image or Video URL (Blob or CDN)
    file?: File;             // Ready-to-attach File (JPEG/MP4)
    audioUrl?: string;       // Audio URL (WAV Blob)
    audioDuration?: number;  // Seconds
    audioTitle?: string;     // Track Name
    genre?: string;
    lyrics?: {
        theme?: string;
        mukhda?: string;
        antra1?: string;
        antra2?: string;
        outro?: string;
    };
    suggestedInstruments?: string[];
    storyboard?: Array<{ scene: number; description: string; duration: string }>;
    modelUsed?: string;
    aspectRatio?: '1:1' | '4:5';
    isRegenerating?: boolean;
}

interface AiInvitationStudioProps {
    eventName: string;
    eventType: string;
    hostName: string;
    eventDate: string;
    eventTime?: string;
    eventVenue: string;
    eventCity: string;
    initialTone: string;
    initialLanguage: string;
    messageTemplate: string;
    onApplyTemplate: (template: string) => void;
    onAttachMedia: (file: File) => Promise<void>;
    isUploadingMedia: boolean;
    mediaUrl: string;
    mediaType: string;
    onRemoveMedia: () => void;
    isLightTheme: boolean;
    showToast: (msg: string, type?: 'success' | 'error' | 'info', duration?: number) => void;
}

// ============================================================================
// AUDIO GENERATION ENGINE (Web Audio API Synthesizer -> 16-bit PCM WAV)
// ============================================================================
function audioBufferToWav(buffer: AudioBuffer): Blob {
    const numOfChan = buffer.numberOfChannels;
    const length = buffer.length * numOfChan * 2 + 44;
    const out = new DataView(new ArrayBuffer(length));
    const channels: Float32Array[] = [];
    const sampleRate = buffer.sampleRate;
    let offset = 0;
    let pos = 0;

    function setUint16(data: number) {
        out.setUint16(pos, data, true);
        pos += 2;
    }
    function setUint32(data: number) {
        out.setUint32(pos, data, true);
        pos += 4;
    }

    // "RIFF"
    out.setUint32(0, 0x46464952, true);
    out.setUint32(4, length - 8, true);
    // "WAVE"
    out.setUint32(8, 0x45564157, true);
    // "fmt " chunk
    out.setUint32(12, 0x20746d66, true);
    out.setUint32(16, 16, true); // 16 for PCM
    setUint16(1); // Linear quantization
    setUint16(numOfChan);
    setUint32(sampleRate);
    setUint32(sampleRate * 2 * numOfChan); // Byte rate
    setUint16(numOfChan * 2); // Block align
    setUint16(16); // 16-bit
    // "data" chunk
    out.setUint32(36, 0x61746164, true);
    out.setUint32(40, length - 44, true);

    pos = 44;
    for (let i = 0; i < buffer.numberOfChannels; i++) {
        channels.push(buffer.getChannelData(i));
    }

    while (offset < buffer.length) {
        for (let i = 0; i < numOfChan; i++) {
            let sample = Math.max(-1, Math.min(1, channels[i][offset]));
            sample = (0.5 + sample < 0 ? sample * 32768 : sample * 32767) | 0;
            out.setInt16(pos, sample, true);
            pos += 2;
        }
        offset++;
    }
    return new Blob([out], { type: 'audio/wav' });
}

function dataUrlToBlob(dataUrl: string): Blob {
    const arr = dataUrl.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
}

function dataUrlToFile(dataUrl: string, filename: string): File {
    const blob = dataUrlToBlob(dataUrl);
    return new File([blob], filename, { type: blob.type });
}

async function synthesizeCelebratorySong(
    eventDetails: { title: string; host: string; eventType: string },
    prompt: string,
    token: string | null,
    anonCount: number,
    variationSeed = 0
): Promise<{
    blob: Blob;
    audioUrl: string;
    duration: number;
    title: string;
    genre?: string;
    lyrics?: {
        theme?: string;
        mukhda?: string;
        antra1?: string;
        antra2?: string;
        outro?: string;
    };
    suggestedInstruments?: string[];
    modelUsed?: string;
    originalPrompt?: string;
    optimizedPrompt?: string;
    enhancementNotes?: string[];
}> {
    // 1. Try Nano Banana Pro backend API
    try {
        const res = await fetch(`${API_BASE_URL}/api/invite/ai/generate-song`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {})
            },
            body: JSON.stringify({
                eventName: eventDetails.title,
                eventType: eventDetails.eventType,
                hostName: eventDetails.host,
                prompt,
                anonymousGenerationsCount: anonCount
            })
        });

        const data = await res.json();
        if (data.success && data.song) {
            const song = data.song;
            const blob = song.audioUrl.startsWith('data:') ? dataUrlToBlob(song.audioUrl) : new Blob();
            return {
                blob,
                audioUrl: song.audioUrl,
                duration: song.duration || 14,
                title: song.title,
                genre: song.genre,
                lyrics: song.lyrics,
                suggestedInstruments: song.suggestedInstruments,
                modelUsed: 'Nano Banana Pro (Audio Maestro)',
                originalPrompt: song.originalPrompt,
                optimizedPrompt: song.optimizedPrompt,
                enhancementNotes: song.enhancementNotes
            };
        }
    } catch (e) {
        console.warn('[synthesizeCelebratorySong] Backend API call failed, falling back to local synthesizer:', e);
    }

    // 2. High-fidelity Client Web Audio Fallback with Curated Wedding Lyrics
    const duration = 12; // 12 seconds
    const sampleRate = 44100;
    const OfflineCtxClass = window.OfflineAudioContext || (window as any).webkitOfflineAudioContext;
    if (!OfflineCtxClass) {
        throw new Error('Web Audio OfflineAudioContext is not supported in this browser.');
    }

    const offlineCtx = new OfflineCtxClass(2, sampleRate * duration, sampleRate);

    // Auspicious celebratory scale (Indian Wedding Raga Bilawal / Bhairavi / Joyous notes)
    const rootFreq = (variationSeed % 2 === 0) ? 293.66 : 261.63; // D4 or C4

    // 1. Tanpura / Warm Festive Drone
    const droneOsc1 = offlineCtx.createOscillator();
    const droneOsc2 = offlineCtx.createOscillator();
    const droneGain = offlineCtx.createGain();
    droneOsc1.type = 'sawtooth';
    droneOsc2.type = 'sine';
    droneOsc1.frequency.setValueAtTime(rootFreq * 0.5, 0);
    droneOsc2.frequency.setValueAtTime(rootFreq * 0.75, 0); // 5th

    const droneFilter = offlineCtx.createBiquadFilter();
    droneFilter.type = 'lowpass';
    droneFilter.frequency.setValueAtTime(450, 0);

    droneGain.gain.setValueAtTime(0.12, 0);
    droneGain.gain.linearRampToValueAtTime(0.02, duration);

    droneOsc1.connect(droneFilter);
    droneOsc2.connect(droneFilter);
    droneFilter.connect(droneGain);
    droneGain.connect(offlineCtx.destination);

    droneOsc1.start(0);
    droneOsc2.start(0);
    droneOsc1.stop(duration);
    droneOsc2.stop(duration);

    // 2. Celebratory Shehnai / Sitar Melodic Sequence
    const scale = [1, 9 / 8, 5 / 4, 4 / 3, 3 / 2, 5 / 3, 15 / 8, 2]; // Bilawal Major scale
    const melodyPattern = (variationSeed % 3 === 0)
        ? [0, 2, 4, 7, 5, 4, 2, 0, 4, 7, 6, 7]
        : (variationSeed % 3 === 1)
        ? [0, 4, 7, 5, 4, 2, 4, 5, 7, 8, 7, 4]
        : [0, 1, 4, 5, 7, 5, 4, 1, 0, 4, 7, 7];

    const noteStep = duration / melodyPattern.length;

    melodyPattern.forEach((degree, i) => {
        const startTime = i * noteStep;
        const noteDuration = noteStep * 0.88;
        const freq = rootFreq * (scale[degree % scale.length] || 1) * (degree >= 7 ? 1.5 : 1);

        const osc = offlineCtx.createOscillator();
        const noteGain = offlineCtx.createGain();
        const filter = offlineCtx.createBiquadFilter();

        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(freq, startTime);
        osc.frequency.linearRampToValueAtTime(freq * 1.015, startTime + noteDuration * 0.5);
        osc.frequency.linearRampToValueAtTime(freq, startTime + noteDuration);

        filter.type = 'bandpass';
        filter.frequency.setValueAtTime(freq * 1.5, startTime);
        filter.Q.setValueAtTime(3.5, startTime);

        noteGain.gain.setValueAtTime(0.001, startTime);
        noteGain.gain.linearRampToValueAtTime(0.22, startTime + 0.05);
        noteGain.gain.exponentialRampToValueAtTime(0.14, startTime + noteDuration * 0.7);
        noteGain.gain.linearRampToValueAtTime(0.001, startTime + noteDuration);

        osc.connect(filter);
        filter.connect(noteGain);
        noteGain.connect(offlineCtx.destination);

        osc.start(startTime);
        osc.stop(startTime + noteDuration);
    });

    // 3. Auspicious Temple Chimes / Celebratory Bells
    const bellTimes = [0, 1.5, 3.0, 4.5, 6.0, 7.5, 9.0, 10.5];
    bellTimes.forEach((t) => {
        if (t >= duration) return;
        const bellOsc = offlineCtx.createOscillator();
        const bellGain = offlineCtx.createGain();
        bellOsc.type = 'sine';
        bellOsc.frequency.setValueAtTime(rootFreq * 3.5, t);
        bellGain.gain.setValueAtTime(0.08, t);
        bellGain.gain.exponentialRampToValueAtTime(0.0001, t + 1.2);

        bellOsc.connect(bellGain);
        bellGain.connect(offlineCtx.destination);

        bellOsc.start(t);
        bellOsc.stop(t + 1.2);
    });

    const renderedBuffer = await offlineCtx.startRendering();
    const wavBlob = audioBufferToWav(renderedBuffer);
    const audioUrl = URL.createObjectURL(wavBlob);

    const titles = [
        `मंगल वेला: ${eventDetails.title} Auspicious Raga`,
        `Royal Shehnai & Sitar Symphony: ${eventDetails.title}`,
        `Vibrant Dholak & Flute Harmony: ${eventDetails.title}`,
        `Golden Mandap Joyous Wedding Prelude: ${eventDetails.title}`
    ];
    const chosenTitle = titles[variationSeed % titles.length];

    const lyrics = {
        theme: `Celebration of ${eventDetails.title}`,
        mukhda: `(Chorus / Mukhda)\nबजे शहनाई, गूंजे ढोल, खुशियों का है आज दौर!\n${eventDetails.title} की यह पावन घड़ी, महक उठा है हर एक छोर!\nमंगल वेला आई रे, शुभ मंगल वेला आई रे! ✨`,
        antra1: `(Verse 1 / Antra)\nअपनों का संग, प्यार का रंग, सजे हैं ख्वाब सुहाने,\nदो दिलों के मिलन की खुशियां, आए सब मिलकर मनाने!\nखुशियों की बरसात हुई, जीवन की नई शुरुआत हुई! 🌸`,
        antra2: `(Verse 2 / Antra)\nतारों से सज के आई बारात, महफ़िल में छाया उल्लास,\nबड़ों के आशीष से पूरा हुआ यह प्यारा एहसास!\nहँसते-गाते झूम उठे हम, संग चलेंगे हर एक कदम! 💖`,
        outro: `(Outro)\nसदा सुहागन, सदा मंगलमय, गूंजे हर ओर तराना,\nशुभ लग्न की इस मंगल घड़ी में, आप सबको है आना!`
    };

    return {
        blob: wavBlob,
        audioUrl,
        duration,
        title: chosenTitle,
        genre: 'Auspicious Shehnai, Sitar & Dholak (Raga Bilawal)',
        lyrics,
        suggestedInstruments: ['Shehnai', 'Sitar', 'Dholak', 'Bansuri (Flute)', 'Tabla', 'Temple Bells'],
        modelUsed: 'Nano Banana Pro Audio Maestro'
    };
}

// ============================================================================
// ============================================================================
// VIDEO GENERATION ENGINE (Nano Banana Pro API + Animated Canvas Recorder)
// ============================================================================
async function generateInvitationVideoTeaser(
    eventDetails: { title: string; subtitle: string; date: string; venue: string; city?: string; host: string; eventType?: string },
    prompt: string,
    token: string | null = null,
    anonCount = 0,
    variationSeed = 0
): Promise<{
    blob: Blob;
    videoUrl: string;
    file: File;
    storyboard?: Array<{ scene: number; description: string; duration: string }>;
    modelUsed?: string;
    originalPrompt?: string;
    optimizedPrompt?: string;
    enhancementNotes?: string[];
}> {
    let aiStoryboard: Array<{ scene: number; description: string; duration: string }> | undefined = undefined;
    let aiOriginalPrompt: string | undefined = undefined;
    let aiOptimizedPrompt: string | undefined = undefined;
    let aiEnhancementNotes: string[] | undefined = undefined;

    // 1. Try Nano Banana Pro backend API for Video Teaser Storyboard & Asset
    try {
        const res = await fetch(`${API_BASE_URL}/api/invite/ai/generate-video`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {})
            },
            body: JSON.stringify({
                eventName: eventDetails.title,
                eventType: eventDetails.eventType || 'wedding',
                hostName: eventDetails.host,
                eventDate: eventDetails.date,
                eventVenue: eventDetails.venue,
                eventCity: eventDetails.city,
                prompt,
                duration: 8,
                anonymousGenerationsCount: anonCount
            })
        });

        const data = await res.json();
        if (data.success && data.video) {
            aiStoryboard = data.video.storyboard;
            aiOriginalPrompt = data.video.originalPrompt;
            aiOptimizedPrompt = data.video.optimizedPrompt;
            aiEnhancementNotes = data.video.enhancementNotes;
            if (data.video.videoUrl && data.video.videoUrl.startsWith('http')) {
                const fetched = await fetch(data.video.videoUrl);
                const blob = await fetched.blob();
                const file = new File([blob], `invitation-teaser-${Date.now()}.mp4`, { type: 'video/mp4' });
                return {
                    blob,
                    videoUrl: data.video.videoUrl,
                    file,
                    storyboard: aiStoryboard,
                    modelUsed: data.video.modelUsed || 'Nano Banana Pro (Gemini Multimodal)',
                    originalPrompt: aiOriginalPrompt,
                    optimizedPrompt: aiOptimizedPrompt,
                    enhancementNotes: aiEnhancementNotes
                };
            }
        }
    } catch (e) {
        console.warn('[generateInvitationVideoTeaser] Backend call failed, using high-fps cinematic canvas renderer:', e);
    }

    const W = 720;
    const H = 720;
    const durationMs = 8000;
    const fps = 30;

    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context unavailable');

    const stream = canvas.captureStream(fps);

    // Determine supported mime type
    const possibleMimes = ['video/mp4', 'video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'];
    let supportedMime = possibleMimes.find(m => (window as any).MediaRecorder && MediaRecorder.isTypeSupported(m)) || 'video/webm';

    // Sparkle particles for cinematic animation
    const particles = Array.from({ length: 45 }, () => ({
        x: Math.random() * W,
        y: Math.random() * H,
        radius: Math.random() * 2.8 + 1,
        speedY: Math.random() * 0.9 + 0.3,
        alpha: Math.random() * 0.8 + 0.2,
        twinkleSpeed: Math.random() * 0.05 + 0.02
    }));

    return new Promise((resolve, reject) => {
        try {
            const recorder = new MediaRecorder(stream, {
                mimeType: supportedMime,
                videoBitsPerSecond: 2500000
            });
            const chunks: Blob[] = [];

            recorder.ondataavailable = (e) => {
                if (e.data && e.data.size > 0) chunks.push(e.data);
            };

            recorder.onstop = () => {
                const videoBlob = new Blob(chunks, { type: supportedMime.split(';')[0] });
                const videoUrl = URL.createObjectURL(videoBlob);
                const ext = supportedMime.includes('mp4') ? 'mp4' : 'webm';
                const file = new File([videoBlob], `invitation-teaser-${Date.now()}.${ext}`, { type: supportedMime.split(';')[0] });
                resolve({
                    blob: videoBlob,
                    videoUrl,
                    file,
                    storyboard: aiStoryboard,
                    modelUsed: 'Nano Banana Pro Cinematic Engine',
                    originalPrompt: aiOriginalPrompt,
                    optimizedPrompt: aiOptimizedPrompt,
                    enhancementNotes: aiEnhancementNotes
                });
            };

            recorder.start();

            const interval = 1000 / fps;
            let currentMs = 0;

            const timer = setInterval(() => {
                currentMs += interval;
                const progress = Math.min(1, currentMs / durationMs);

                // --- Draw Cinematic Frame ---
                // 1. Dynamic background gradient
                const grad = ctx.createLinearGradient(0, 0, W, H);
                if (variationSeed % 2 === 0) {
                    grad.addColorStop(0, '#2D030D');
                    grad.addColorStop(0.5, '#450614');
                    grad.addColorStop(1, '#1A0006');
                } else {
                    grad.addColorStop(0, '#09122C');
                    grad.addColorStop(0.5, '#0F1D45');
                    grad.addColorStop(1, '#030712');
                }
                ctx.fillStyle = grad;
                ctx.fillRect(0, 0, W, H);

                // Radial spotlight pulse
                const pulse = Math.sin(progress * Math.PI * 4) * 0.1 + 0.9;
                const radial = ctx.createRadialGradient(W / 2, H / 2, 20, W / 2, H / 2, W * 0.6 * pulse);
                radial.addColorStop(0, 'rgba(245, 158, 11, 0.18)');
                radial.addColorStop(0.6, 'rgba(245, 158, 11, 0.03)');
                radial.addColorStop(1, 'rgba(0, 0, 0, 0.5)');
                ctx.fillStyle = radial;
                ctx.fillRect(0, 0, W, H);

                // 2. Animated floating golden particles
                particles.forEach(p => {
                    p.y -= p.speedY;
                    if (p.y < 0) p.y = H;
                    p.alpha += Math.sin(currentMs * p.twinkleSpeed) * 0.05;
                    const clampedAlpha = Math.max(0.1, Math.min(0.9, p.alpha));

                    ctx.save();
                    ctx.fillStyle = `rgba(245, 218, 122, ${clampedAlpha})`;
                    ctx.beginPath();
                    ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
                    ctx.fill();
                    ctx.restore();
                });

                // 3. Ornate Glowing Gold Borders
                const borderAlpha = Math.min(1, progress * 2.5);
                ctx.save();
                ctx.strokeStyle = `rgba(245, 158, 11, ${borderAlpha})`;
                ctx.lineWidth = 3;
                ctx.strokeRect(30, 30, W - 60, H - 60);

                ctx.strokeStyle = `rgba(255, 255, 255, ${borderAlpha * 0.4})`;
                ctx.lineWidth = 1;
                ctx.strokeRect(40, 40, W - 80, H - 80);

                // Corner Diamonds
                const drawCornerDiamond = (x: number, y: number) => {
                    ctx.save();
                    ctx.translate(x, y);
                    ctx.rotate(Math.PI / 4);
                    ctx.fillStyle = '#F59E0B';
                    ctx.fillRect(-6, -6, 12, 12);
                    ctx.restore();
                };
                drawCornerDiamond(30, 30);
                drawCornerDiamond(W - 30, 30);
                drawCornerDiamond(30, H - 30);
                drawCornerDiamond(W - 30, H - 30);

                // 4. Animated Motif Ring (scales & rotates gently)
                const motifScale = Math.min(1, progress * 2);
                ctx.save();
                ctx.translate(W / 2, 110);
                ctx.scale(motifScale, motifScale);
                ctx.strokeStyle = '#F59E0B';
                ctx.lineWidth = 1.5;
                ctx.beginPath();
                ctx.arc(0, 0, 36, 0, Math.PI * 2);
                ctx.stroke();

                ctx.font = '36px "Segoe UI Emoji", "Apple Color Emoji", sans-serif';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                const motifIcon = (prompt.toLowerCase().includes('cocktail') || prompt.toLowerCase().includes('party'))
                    ? '🥂'
                    : (prompt.toLowerCase().includes('ganesh') ? '🕉️' : '💍');
                ctx.fillText(motifIcon, 0, 0);
                ctx.restore();

                // 5. Animated Headline & Couple Name (Fade & Glide In)
                const titleAlpha = Math.max(0, Math.min(1, (progress - 0.15) * 3));
                ctx.save();
                ctx.textAlign = 'center';

                // Subtitle / Headline
                ctx.font = '600 14px "Inter", "Cinzel", sans-serif';
                ctx.fillStyle = `rgba(245, 158, 11, ${titleAlpha})`;
                ctx.letterSpacing = '4px';
                ctx.fillText((eventDetails.subtitle || 'CORDIALLY INVITE YOU TO CELEBRATE').toUpperCase(), W / 2, 185);

                // Main Title / Names (with metallic shine)
                ctx.shadowColor = 'rgba(0,0,0,0.8)';
                ctx.shadowBlur = 12;
                ctx.font = 'bold 44px "Playfair Display", "Cinzel", Georgia, serif';
                ctx.fillStyle = `rgba(255, 255, 255, ${titleAlpha})`;
                ctx.fillText(eventDetails.title || 'Our Celebration', W / 2, 255);

                // Decorative Divider
                ctx.strokeStyle = `rgba(245, 158, 11, ${titleAlpha * 0.7})`;
                ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.moveTo(W / 2 - 90, 285);
                ctx.lineTo(W / 2 + 90, 285);
                ctx.stroke();
                drawCornerDiamond(W / 2, 285);

                // 6. Date & Time Pill Badge (Animates in at 40% progress)
                const badgeAlpha = Math.max(0, Math.min(1, (progress - 0.35) * 3));
                const badgeW = 460;
                const badgeH = 54;
                const badgeX = (W - badgeW) / 2;
                const badgeY = 360;

                ctx.fillStyle = `rgba(0, 0, 0, ${badgeAlpha * 0.45})`;
                ctx.strokeStyle = `rgba(245, 158, 11, ${badgeAlpha})`;
                ctx.lineWidth = 1.5;
                ctx.beginPath();
                ctx.roundRect(badgeX, badgeY - badgeH / 2, badgeW, badgeH, 27);
                ctx.fill();
                ctx.stroke();

                ctx.font = 'bold 18px "Inter", sans-serif';
                ctx.fillStyle = `rgba(255, 255, 255, ${badgeAlpha})`;
                ctx.letterSpacing = '1px';
                ctx.fillText(`📅  ${eventDetails.date || 'Date to be announced'}`, W / 2, badgeY + 2);

                // 7. Venue & City
                const venueAlpha = Math.max(0, Math.min(1, (progress - 0.5) * 3));
                ctx.font = 'bold 18px "Inter", sans-serif';
                ctx.fillStyle = `rgba(245, 158, 11, ${venueAlpha})`;
                ctx.letterSpacing = '2px';
                ctx.fillText(`📍 ${eventDetails.venue.toUpperCase() || 'GRAND VENUE'}`, W / 2, 455);

                // 8. Host & RSVP
                const hostAlpha = Math.max(0, Math.min(1, (progress - 0.65) * 3));
                ctx.font = '600 15px "Inter", sans-serif';
                ctx.fillStyle = `rgba(226, 232, 240, ${hostAlpha})`;
                ctx.letterSpacing = '2px';
                ctx.fillText(eventDetails.host || 'KINDLY RSVP • THE FAMILY', W / 2, 530);

                // 9. Watermark Footer
                ctx.font = '600 12px "Inter", sans-serif';
                ctx.fillStyle = 'rgba(255, 255, 255, 0.45)';
                ctx.letterSpacing = '3px';
                ctx.fillText('CINEMATIC INVITATION TEASER  •  NANO BANANA PRO', W / 2, H - 50);

                ctx.restore();

                if (currentMs >= durationMs) {
                    clearInterval(timer);
                    setTimeout(() => recorder.stop(), 200);
                }
            }, interval);
        } catch (err) {
            reject(err);
        }
    });
}

// ============================================================================
// IMAGE GENERATION ENGINE (Nano Banana Pro API + High-Res Canvas Fallback)
// ============================================================================
async function generateInvitationPoster(
    eventDetails: { title: string; subtitle: string; date: string; venue: string; city?: string; host: string; eventType?: string },
    prompt: string,
    token: string | null = null,
    anonCount = 0,
    variationSeed = 0
): Promise<{
    blob: Blob;
    imageUrl: string;
    file: File;
    modelUsed?: string;
    originalPrompt?: string;
    optimizedPrompt?: string;
    enhancementNotes?: string[];
}> {
    // 1. Try Nano Banana Pro backend API
    try {
        const res = await fetch(`${API_BASE_URL}/api/invite/ai/generate-image`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {})
            },
            body: JSON.stringify({
                eventName: eventDetails.title,
                eventType: eventDetails.eventType || 'wedding',
                hostName: eventDetails.host,
                eventDate: eventDetails.date,
                eventVenue: eventDetails.venue,
                eventCity: eventDetails.city,
                prompt,
                aspectRatio: '1:1',
                anonymousGenerationsCount: anonCount
            })
        });

        const data = await res.json();
        if (data.success && data.image && data.image.imageUrl) {
            const imgData = data.image;
            let file: File;
            let blob: Blob;
            if (imgData.imageUrl.startsWith('data:')) {
                file = dataUrlToFile(imgData.imageUrl, `nano-banana-pro-${Date.now()}.jpg`);
                blob = file;
            } else {
                const fetched = await fetch(imgData.imageUrl);
                blob = await fetched.blob();
                file = new File([blob], `nano-banana-pro-${Date.now()}.jpg`, { type: 'image/jpeg' });
            }
            return {
                blob,
                imageUrl: imgData.imageUrl,
                file,
                modelUsed: imgData.modelUsed || 'Nano Banana Pro (Gemini 3 Pro Image)',
                originalPrompt: imgData.originalPrompt,
                optimizedPrompt: imgData.optimizedPrompt,
                enhancementNotes: imgData.enhancementNotes
            };
        }
    } catch (e) {
        console.warn('[generateInvitationPoster] Backend API failed, falling back to local canvas poster:', e);
    }

    const W = 1080;
    const H = 1080;
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D unavailable');

    const promptLower = prompt.toLowerCase();

    // Determine aesthetic palette based on prompt
    let bgColors = ['#2D030D', '#450614', '#1A0006']; // Royal Crimson
    let goldColor = '#F59E0B';
    let motif = '💍';

    if (promptLower.includes('emerald') || promptLower.includes('green') || promptLower.includes('marigold')) {
        bgColors = ['#042718', '#073B25', '#02140C'];
        goldColor = '#FCD34D';
        motif = '🪔';
    } else if (promptLower.includes('navy') || promptLower.includes('blue') || promptLower.includes('midnight')) {
        bgColors = ['#09122C', '#0F1D45', '#030712'];
        goldColor = '#E0E7FF';
        motif = '✨';
    } else if (promptLower.includes('pastel') || promptLower.includes('pink') || promptLower.includes('blush') || promptLower.includes('floral')) {
        bgColors = ['#2C1324', '#3A1830', '#150812'];
        goldColor = '#F472B6';
        motif = '🪷';
    } else if (promptLower.includes('ivory') || promptLower.includes('gold') || promptLower.includes('vintage')) {
        bgColors = ['#261F18', '#362C22', '#140F0B'];
        goldColor = '#FBBF24';
        motif = '👑';
    } else if (promptLower.includes('cocktail') || promptLower.includes('party') || promptLower.includes('sangeet')) {
        bgColors = ['#380B35', '#5C1438', '#1F0510'];
        goldColor = '#FB7185';
        motif = '🥂';
    } else if (variationSeed % 3 === 1) {
        bgColors = ['#09122C', '#0F1D45', '#030712'];
        motif = '✨';
    } else if (variationSeed % 3 === 2) {
        bgColors = ['#042718', '#073B25', '#02140C'];
        motif = '🕉️';
    }

    // 1. Draw Background
    const bgGrad = ctx.createLinearGradient(0, 0, W, H);
    bgGrad.addColorStop(0, bgColors[0]);
    bgGrad.addColorStop(0.5, bgColors[1]);
    bgGrad.addColorStop(1, bgColors[2]);
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, W, H);

    // Radial center glow
    const spot = ctx.createRadialGradient(W / 2, H / 2, 40, W / 2, H / 2, W * 0.65);
    spot.addColorStop(0, 'rgba(255, 255, 255, 0.08)');
    spot.addColorStop(0.6, 'rgba(255, 255, 255, 0.02)');
    spot.addColorStop(1, 'rgba(0, 0, 0, 0.45)');
    ctx.fillStyle = spot;
    ctx.fillRect(0, 0, W, H);

    // 2. Ornate Multi-layer Borders
    const margin = 45;
    const innerMargin = 60;
    ctx.save();
    ctx.strokeStyle = goldColor;
    ctx.lineWidth = 3.5;
    ctx.strokeRect(margin, margin, W - margin * 2, H - margin * 2);

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
    ctx.lineWidth = 1;
    ctx.strokeRect(innerMargin, innerMargin, W - innerMargin * 2, H - innerMargin * 2);

    const drawDiamond = (x: number, y: number, sz = 12) => {
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(Math.PI / 4);
        ctx.fillStyle = goldColor;
        ctx.fillRect(-sz / 2, -sz / 2, sz, sz);
        ctx.restore();
    };

    drawDiamond(margin, margin);
    drawDiamond(W - margin, margin);
    drawDiamond(margin, H - margin);
    drawDiamond(W - margin, H - margin);
    drawDiamond(W / 2, margin, 10);
    drawDiamond(W / 2, H - margin, 10);

    // 3. Top Motif Halo
    const motifY = 150;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(W / 2, motifY, 48, 0, Math.PI * 2);
    ctx.stroke();

    ctx.strokeStyle = goldColor;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(W / 2, motifY, 54, 0, Math.PI * 2);
    ctx.stroke();

    ctx.font = '50px "Segoe UI Emoji", "Apple Color Emoji", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(motif, W / 2, motifY);

    // 4. Headline
    ctx.font = '600 20px "Inter", "Cinzel", Georgia, sans-serif';
    ctx.fillStyle = goldColor;
    ctx.letterSpacing = '5px';
    ctx.fillText((eventDetails.subtitle || 'TOGETHER WITH THEIR FAMILIES').toUpperCase(), W / 2, 255);

    // Divider
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(W / 2 - 130, 285);
    ctx.lineTo(W / 2 + 130, 285);
    ctx.stroke();
    drawDiamond(W / 2, 285, 7);

    // 5. Main Title / Couple Names
    ctx.shadowColor = 'rgba(0, 0, 0, 0.65)';
    ctx.shadowBlur = 18;
    ctx.shadowOffsetY = 4;
    ctx.font = 'bold 64px "Playfair Display", "Cinzel", Georgia, serif';
    ctx.fillStyle = '#FFFFFF';
    ctx.letterSpacing = '1px';
    ctx.fillText(eventDetails.title, W / 2, 380);
    ctx.restore();

    // 6. Subtitle
    ctx.font = '500 19px "Inter", sans-serif';
    ctx.fillStyle = '#CBD5E1';
    ctx.letterSpacing = '3px';
    ctx.fillText('CORDIALLY INVITE YOU TO CELEBRATE THEIR WEDDING', W / 2, 455);

    // 7. Date & Time Badge
    const badgeW = 680;
    const badgeH = 80;
    const badgeX = (W - badgeW) / 2;
    const badgeY = 570;

    ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
    ctx.strokeStyle = goldColor;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.roundRect(badgeX, badgeY - badgeH / 2, badgeW, badgeH, 40);
    ctx.fill();
    ctx.stroke();

    ctx.font = 'bold 24px "Inter", sans-serif';
    ctx.fillStyle = '#F8FAFC';
    ctx.letterSpacing = '1px';
    ctx.fillText(`📅  ${eventDetails.date}`, W / 2, badgeY + 2);

    // 8. Venue & Location
    ctx.font = 'bold 24px "Inter", sans-serif';
    ctx.fillStyle = goldColor;
    ctx.letterSpacing = '2px';
    ctx.fillText(`📍 ${eventDetails.venue.toUpperCase()}`, W / 2, 690);

    // 9. RSVP & Host Tagline
    ctx.font = '600 18px "Inter", sans-serif';
    ctx.fillStyle = '#E2E8F0';
    ctx.letterSpacing = '2px';
    ctx.fillText(eventDetails.host, W / 2, 770);

    // 10. Footer Brand
    const footerY = 960;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(margin + 50, footerY - 35);
    ctx.lineTo(W - margin - 50, footerY - 35);
    ctx.stroke();
    drawDiamond(W / 2, footerY - 35, 7);

    ctx.font = '600 15px "Inter", sans-serif';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.55)';
    ctx.letterSpacing = '2px';
    ctx.fillText('INVITATION STUDIO  •  SHADIGRAM.COM', W / 2, footerY);

    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (!blob) {
                reject(new Error('Canvas image blob generation failed'));
                return;
            }
            const imageUrl = URL.createObjectURL(blob);
            const file = new File([blob], `invitation-poster-${Date.now()}.jpg`, { type: 'image/jpeg' });
            resolve({ blob, imageUrl, file });
        }, 'image/jpeg', 0.95);
    });
}

// ============================================================================
// TEXT GENERATION ENGINE (API Call with Rich Fallbacks)
// ============================================================================
async function generateInvitationText(
    eventDetails: { title: string; eventType: string; host: string; date: string; venue: string; city: string },
    prompt: string,
    token: string | null,
    anonCount: number,
    variationSeed = 0
): Promise<{
    text: string;
    title: string;
    limitReached?: boolean;
    originalPrompt?: string;
    optimizedPrompt?: string;
    enhancementNotes?: string[];
}> {
    const promptLower = prompt.toLowerCase();

    // 1. Try Backend API
    try {
        const res = await fetch(`${API_BASE_URL}/api/invite/ai/generate-text`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {})
            },
            body: JSON.stringify({
                eventType: eventDetails.eventType || 'wedding',
                eventName: eventDetails.title,
                hostName: eventDetails.host,
                eventDate: eventDetails.date,
                eventVenue: eventDetails.venue,
                eventCity: eventDetails.city,
                tone: promptLower.includes('royal') ? 'formal' : (promptLower.includes('party') || promptLower.includes('fun') ? 'casual' : 'warm'),
                language: promptLower.includes('hindi') ? 'hi' : 'en',
                customPrompt: prompt.trim(),
                anonymousGenerationsCount: anonCount
            })
        });

        const data = await res.json();
        if (res.status === 403 || data.limitReached) {
            return { text: '', title: '', limitReached: true };
        }

        if (data.success && Array.isArray(data.drafts) && data.drafts.length > 0) {
            const chosen = data.drafts[variationSeed % data.drafts.length];
            return {
                text: chosen.content,
                title: chosen.title,
                originalPrompt: chosen.originalPrompt,
                optimizedPrompt: chosen.optimizedPrompt,
                enhancementNotes: chosen.enhancementNotes
            };
        }
    } catch (e) {
        // Fall back to client generator
    }

    // 2. Intelligent Client Generator
    const title = eventDetails.title || 'Our Wedding Celebration';
    const host = eventDetails.host || 'The Family';
    const custom = prompt.trim() ? `\n\n📌 Special Note: ${prompt.trim()}` : '';

    if (promptLower.includes('royal') || promptLower.includes('formal') || promptLower.includes('traditional') || variationSeed % 3 === 1) {
        return {
            title: '👑 Royal & Majestic Traditional Wording',
            text: `Honored {{recipientName}},\n\nTogether with their families, ${host} takes immense pleasure in requesting the honor of your gracious presence to celebrate the auspicious occasion of ${title}.\n\n📅 Date & Time: {{date}}\n📍 Grand Venue: {{venue}}, {{city}}\n\nKindly confirm your valued presence using the link below:\n👉 {{rsvpLink}}${custom}\n\nWith high regards,\n${host}`
        };
    } else if (promptLower.includes('party') || promptLower.includes('cocktail') || promptLower.includes('fun') || promptLower.includes('peppy') || variationSeed % 3 === 2) {
        return {
            title: '🎉 Vibrant Party & Cocktails Wording',
            text: `Hey {{recipientName}}! 🥂✨\n\nThe date is set, the drinks are chilled, and the dance floor is waiting! We are thrilled to invite you to celebrate ${title}. Come ready to make unforgettable memories, eat amazing food, and dance the night away!\n\n📅 When: {{date}}\n📍 Where: {{venue}}, {{city}}\n\nHit the RSVP link right here to confirm your spot:\n👉 {{rsvpLink}}${custom}\n\nCan't wait to celebrate with you!\nCheers, ${host}`
        };
    } else {
        return {
            title: '💖 Heartfelt & Warm Family Blessing Wording',
            text: `Dear {{recipientName}},\n\nWith immense love in our hearts and gratitude in our souls, we invite you to join us in celebrating ${title}. Moments like these are truly made special only when surrounded by the people we cherish most.\n\n📅 Date: {{date}}\n📍 Venue: {{venue}}, {{city}}\n\nPlease let us know if you can make it by tapping the RSVP link below:\n👉 {{rsvpLink}}${custom}\n\nWith warmest regards,\n${host} & Family`
        };
    }
}

// ============================================================================
// MAIN COMPONENT: AI INVITATION STUDIO (Gemini / ChatGPT Chat Interface)
// ============================================================================
export const AiInvitationStudio: React.FC<AiInvitationStudioProps> = ({
    eventName,
    eventType,
    hostName,
    eventDate,
    eventTime,
    eventVenue,
    eventCity,
    initialTone,
    initialLanguage,
    messageTemplate,
    onApplyTemplate,
    onAttachMedia,
    isUploadingMedia,
    mediaUrl,
    mediaType,
    onRemoveMedia,
    isLightTheme,
    showToast,
}) => {
    // Auth & Quota
    const { inviteUser, inviteToken, upgradeAiPlan, createCashfreeOrder, verifyCashfreePayment, refreshProfile } = useInviteAuth();
    const [anonCount, setAnonCount] = useState<number>(() => {
        const stored = localStorage.getItem('shadigram_nanobanana_ai_count');
        return stored ? Number(stored) : 0;
    });
    const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState<boolean>(false);
    const [modalSelectedPlan, setModalSelectedPlan] = useState<'ai_unlimited' | 'ai_plus'>('ai_plus');
    const [isUpgrading, setIsUpgrading] = useState<boolean>(false);

    const hasAiPlan = Boolean(inviteUser?.hasAiPlan);
    const usedCount = inviteUser ? (inviteUser.aiGenerationsUsed || 0) : anonCount;
    const remainingGenerations = hasAiPlan ? 999999 : Math.max(0, 5 - usedCount);
    const isLimitReached = !hasAiPlan && remainingGenerations <= 0;

    // Chat State
    const [selectedMode, setSelectedMode] = useState<GenAiMode>('text');
    const [inputPrompt, setInputPrompt] = useState<string>('');
    const [isGenerating, setIsGenerating] = useState<boolean>(false);
    const [isOptimizingPrompt, setIsOptimizingPrompt] = useState<boolean>(false);
    const [activeAudioPlayingId, setActiveAudioPlayingId] = useState<string | null>(null);
    const audioPlayerRef = useRef<HTMLAudioElement | null>(null);
    const chatEndRef = useRef<HTMLDivElement | null>(null);

    // Initial Chat State
    const [messages, setMessages] = useState<ChatMessage[]>(() => [
        {
            id: 'init-welcome',
            role: 'assistant',
            mode: 'text',
            prompt: 'Welcome',
            timestamp: new Date(),
            content: `Hello! I am **Nano Banana Pro 🍌⚡**, your dedicated Gemini Multimodal Gen AI Invitation Designer.\n\nI have loaded your celebration context:\n• **Event:** ${eventName || 'Our Celebration'}\n• **Type:** ${eventType || 'Wedding'}\n• **Date:** ${eventDate || 'Date to be announced'}\n• **Venue:** ${[eventVenue, eventCity].filter(Boolean).join(', ') || 'Venue TBA'}\n\nSelect a generation mode below (**Text / Scripts**, **Image Posters**, **Cinematic Videos**, or **Celebratory Songs & Lyrics**) or type any custom request to craft your dream invitation! ✨`
        }
    ]);

    // Format date nicely
    const formattedDate = React.useMemo(() => {
        if (!eventDate) return 'Date to be announced';
        try {
            const parts = eventDate.split('-').map(Number);
            if (parts.length === 3 && !parts.some(isNaN)) {
                const dt = new Date(parts[0], parts[1] - 1, parts[2]);
                const ds = dt.toLocaleDateString('en-US', {
                    weekday: 'short',
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                });
                return eventTime ? `${ds} • ${eventTime}` : ds;
            }
        } catch (e) {}
        return eventTime ? `${eventDate} • ${eventTime}` : eventDate;
    }, [eventDate, eventTime]);

    const formattedVenue = React.useMemo(() => {
        const parts = [eventVenue, eventCity].filter(Boolean);
        return parts.length ? parts.join(', ') : 'Venue & City TBA';
    }, [eventVenue, eventCity]);

    const formattedHost = React.useMemo(() => {
        return hostName ? `KINDLY RSVP • ${hostName.toUpperCase()}` : 'KINDLY RSVP • THE FAMILY';
    }, [hostName]);

    // Auto-scroll chat to bottom
    const scrollToBottom = useCallback(() => {
        setTimeout(() => {
            chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    }, []);

    // Record generation count in DB / local storage
    const recordGenAiUsage = async () => {
        if (hasAiPlan) return;
        try {
            const token = inviteToken || localStorage.getItem('invite_accessToken') || localStorage.getItem('inviteToken');
            if (token) {
                await fetch(`${API_BASE_URL}/api/invite/ai/record-usage`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    }
                });
                await refreshProfile();
            } else {
                const next = anonCount + 1;
                setAnonCount(next);
                localStorage.setItem('shadigram_nanobanana_ai_count', String(next));
            }
        } catch (e) {}
    };

    // Prompt Optimizer: Enhances user prompt before sending over LLM
    const handleOptimizePrompt = async () => {
        const textToOptimize = inputPrompt.trim();
        setIsOptimizingPrompt(true);
        try {
            const token = inviteToken || localStorage.getItem('invite_accessToken') || localStorage.getItem('inviteToken');
            const res = await fetch(`${API_BASE_URL}/api/invite/ai/optimize-prompt`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {})
                },
                body: JSON.stringify({
                    modality: selectedMode === 'text' ? 'script' : selectedMode,
                    prompt: textToOptimize,
                    eventName: eventName || 'Our Royal Celebration',
                    eventType: eventType || 'wedding',
                    language: 'en'
                })
            });
            const data = await res.json();
            if (data.success && data.optimizedPrompt) {
                setInputPrompt(data.optimizedPrompt);
                showToast('✨ Prompt polished & enriched with luxury wedding aesthetics!', 'success');
            } else {
                showToast('Could not optimize prompt. Please try again.', 'info');
            }
        } catch (e: any) {
            showToast('Prompt optimization service busy, keeping original prompt.', 'info');
        } finally {
            setIsOptimizingPrompt(false);
        }
    };

    // --- MAIN GENERATION HANDLER ---
    const handleGenerate = async (customPromptOverride?: string, customModeOverride?: GenAiMode, isRegenerate = false, targetMsgId?: string) => {
        const mode = customModeOverride || selectedMode;
        const promptText = (customPromptOverride !== undefined ? customPromptOverride : inputPrompt).trim();

        if (!promptText && !isRegenerate) {
            showToast('Please type a prompt or choose a starter option below!', 'info');
            return;
        }

        if (isLimitReached) {
            setIsUpgradeModalOpen(true);
            showToast('🍌 You have reached your 5 free Gen AI generations!', 'info');
            return;
        }

        setIsGenerating(true);

        const promptLabel = promptText || (mode === 'text' ? 'Invitation wording' : mode === 'image' ? 'Royal poster' : mode === 'video' ? 'Cinematic teaser' : 'Festive theme song');

        // Add user prompt bubble if not in-place regenerate
        if (!isRegenerate) {
            const userMsg: ChatMessage = {
                id: `user-${Date.now()}`,
                role: 'user',
                mode,
                prompt: promptLabel,
                timestamp: new Date()
            };
            setMessages(prev => [...prev, userMsg]);
            setInputPrompt('');
            scrollToBottom();
        }

        const variationSeed = Math.floor(Math.random() * 1000);
        const eventData = {
            title: eventName || 'Our Celebration',
            subtitle: 'CORDIALLY INVITE YOU TO CELEBRATE',
            date: formattedDate,
            venue: formattedVenue,
            city: eventCity || '',
            host: formattedHost,
            eventType: eventType || 'wedding'
        };

        try {
            let newAssistantMsg: ChatMessage;

            if (mode === 'text') {
                const token = inviteToken || localStorage.getItem('invite_accessToken') || localStorage.getItem('inviteToken');
                const result = await generateInvitationText(eventData, promptLabel, token, anonCount, variationSeed);

                if (result.limitReached) {
                    setIsUpgradeModalOpen(true);
                    setIsGenerating(false);
                    return;
                }

                newAssistantMsg = {
                    id: targetMsgId || `ai-text-${Date.now()}`,
                    role: 'assistant',
                    mode: 'text',
                    prompt: promptLabel,
                    originalPrompt: result.originalPrompt || promptLabel,
                    optimizedPrompt: result.optimizedPrompt,
                    enhancementNotes: result.enhancementNotes,
                    timestamp: new Date(),
                    content: result.text,
                    modelUsed: 'Nano Banana Pro 🍌⚡'
                };
            } else if (mode === 'image') {
                const token = inviteToken || localStorage.getItem('invite_accessToken') || localStorage.getItem('inviteToken');
                const result = await generateInvitationPoster(eventData, promptLabel, token, anonCount, variationSeed);
                newAssistantMsg = {
                    id: targetMsgId || `ai-img-${Date.now()}`,
                    role: 'assistant',
                    mode: 'image',
                    prompt: promptLabel,
                    originalPrompt: result.originalPrompt || promptLabel,
                    optimizedPrompt: result.optimizedPrompt,
                    enhancementNotes: result.enhancementNotes,
                    timestamp: new Date(),
                    mediaUrl: result.imageUrl,
                    file: result.file,
                    modelUsed: result.modelUsed || 'Nano Banana Pro (Gemini 3 Pro Image)'
                };
            } else if (mode === 'video') {
                const token = inviteToken || localStorage.getItem('invite_accessToken') || localStorage.getItem('inviteToken');
                const result = await generateInvitationVideoTeaser(eventData, promptLabel, token, anonCount, variationSeed);
                newAssistantMsg = {
                    id: targetMsgId || `ai-vid-${Date.now()}`,
                    role: 'assistant',
                    mode: 'video',
                    prompt: promptLabel,
                    originalPrompt: result.originalPrompt || promptLabel,
                    optimizedPrompt: result.optimizedPrompt,
                    enhancementNotes: result.enhancementNotes,
                    timestamp: new Date(),
                    mediaUrl: result.videoUrl,
                    file: result.file,
                    storyboard: result.storyboard,
                    modelUsed: result.modelUsed || 'Nano Banana Pro Cinematic Engine'
                };
            } else {
                // Song
                const token = inviteToken || localStorage.getItem('invite_accessToken') || localStorage.getItem('inviteToken');
                const result = await synthesizeCelebratorySong(eventData, promptLabel, token, anonCount, variationSeed);
                newAssistantMsg = {
                    id: targetMsgId || `ai-song-${Date.now()}`,
                    role: 'assistant',
                    mode: 'song',
                    prompt: promptLabel,
                    originalPrompt: result.originalPrompt || promptLabel,
                    optimizedPrompt: result.optimizedPrompt,
                    enhancementNotes: result.enhancementNotes,
                    timestamp: new Date(),
                    audioUrl: result.audioUrl,
                    audioDuration: result.duration,
                    audioTitle: result.title,
                    genre: result.genre,
                    lyrics: result.lyrics,
                    suggestedInstruments: result.suggestedInstruments,
                    modelUsed: result.modelUsed || 'Nano Banana Pro Audio Maestro'
                };
            }

            await recordGenAiUsage();

            if (isRegenerate && targetMsgId) {
                setMessages(prev => prev.map(m => m.id === targetMsgId ? newAssistantMsg : m));
                showToast(`✨ Regenerated ${mode.toUpperCase()}! (Powered by Nano Banana Pro 🍌⚡)`, 'success');
            } else {
                setMessages(prev => [...prev, newAssistantMsg]);
                showToast(`✨ Generated ${mode.toUpperCase()}! (Powered by Nano Banana Pro 🍌⚡)`, 'success');
            }

            scrollToBottom();
        } catch (err: any) {
            console.error('[Gen AI Error]:', err);
            showToast(`Could not generate ${mode}: ${err.message || 'Please try again.'}`, 'error');
        } finally {
            setIsGenerating(false);
        }
    };

    // Audio Play / Pause handler
    const handleToggleAudio = (audioUrl: string, id: string) => {
        if (activeAudioPlayingId === id && audioPlayerRef.current) {
            audioPlayerRef.current.pause();
            setActiveAudioPlayingId(null);
            return;
        }

        if (audioPlayerRef.current) {
            audioPlayerRef.current.pause();
        }

        const audio = new Audio(audioUrl);
        audioPlayerRef.current = audio;
        audio.play().then(() => {
            setActiveAudioPlayingId(id);
        }).catch((e) => {
            console.warn('[Audio Play Failed]:', e);
        });

        audio.onended = () => {
            setActiveAudioPlayingId(null);
        };
    };

    // Download handler for Audio, Image, Video
    const handleDownloadMedia = (url: string, filename: string) => {
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        showToast(`📥 Downloaded ${filename}!`, 'success');
    };

    // Copy Text Draft
    const handleCopyText = (text: string) => {
        navigator.clipboard.writeText(text);
        showToast('📋 Copied invitation text to clipboard!', 'info', 2000);
    };

    // Clear / Reset Chat
    const handleResetChat = () => {
        setMessages([
            {
                id: 'init-welcome',
                role: 'assistant',
                mode: 'text',
                prompt: 'Welcome',
                timestamp: new Date(),
                content: `Chat reset! Let's craft something amazing for **${eventName || 'your celebration'}**.\n\nChoose what to generate below:`
            }
        ]);
        showToast('Chat history cleared.', 'info');
    };

    // Upgrade AI Plan (Cashfree or Simulator)
    const handleUpgradeToAiPlan = async (isTestSimulate = false) => {
        setIsUpgrading(true);
        const planToBuy = modalSelectedPlan;
        try {
            if (!inviteToken) {
                showToast('Please sign in or register in Step 3 to link your AI Plan!', 'info');
                if (isTestSimulate) {
                    setAnonCount(0);
                    localStorage.setItem('shadigram_nanobanana_ai_count', '0');
                    showToast('🍌 Nano Banana AI Plan activated for this session!', 'success');
                    setIsUpgradeModalOpen(false);
                }
                return;
            }

            if (isTestSimulate) {
                if (planToBuy === 'ai_plus') {
                    const orderData = await createCashfreeOrder('ai_plus', true);
                    await verifyCashfreePayment(orderData.orderId, 'ai_plus', true);
                    await refreshProfile();
                    showToast('👑🍌 AI+ Royal VIP Plan activated successfully!', 'success');
                } else {
                    await upgradeAiPlan();
                    await refreshProfile();
                    showToast('🍌 Nano Banana AI Unlimited Plan activated successfully!', 'success');
                }
                setIsUpgradeModalOpen(false);
                return;
            }

            // Real Cashfree Gateway Checkout
            const orderData = await createCashfreeOrder(planToBuy, false);
            if (orderData.isMock || orderData.paymentSessionId.startsWith('session_mock_')) {
                await verifyCashfreePayment(orderData.orderId, planToBuy, true);
                await refreshProfile();
                showToast(planToBuy === 'ai_plus' ? '👑 AI+ Royal VIP Plan activated!' : '👑 Nano Banana AI Unlimited Plan activated!', 'success');
                setIsUpgradeModalOpen(false);
                return;
            }

            const checkoutResult = await openCashfreeCheckout({
                paymentSessionId: orderData.paymentSessionId,
                environment: orderData.environment || 'sandbox',
                redirectTarget: '_modal'
            });

            if (!checkoutResult.success && checkoutResult.error) {
                throw new Error(checkoutResult.error);
            }

            await verifyCashfreePayment(orderData.orderId, planToBuy, false);
            await refreshProfile();
            showToast(planToBuy === 'ai_plus' ? '👑 AI+ Royal VIP Plan activated!' : '👑 Nano Banana AI Unlimited Plan activated!', 'success');
            setIsUpgradeModalOpen(false);
        } catch (err: any) {
            console.error('[AI Plan Upgrade Error]:', err);
            showToast(err.message || 'Payment could not be completed.', 'error');
        } finally {
            setIsUpgrading(false);
        }
    };

    // Mode-specific prompt starter chips
    const promptStarters: Record<GenAiMode, string[]> = {
        text: [
            '👑 Royal Sanskrit & English Regal Wedding Script',
            '💖 Heartfelt Family Love & Blessing Invitation',
            '✨ Poetic Shayari Matrimonial Verse',
            '🎉 Bollywood Sangeet & Cocktail Dhamaal Script'
        ],
        image: [
            '🏰 Royal Palace 4K Wedding Invitation Poster',
            '🪷 Sacred Lotus Mandap & 3D Embossed Gold Foil Card',
            '✨ Midnight Navy Celestial Starlight Luxury Poster'
        ],
        video: [
            '🎬 10s Royal Cinematic Mandap Teaser with Golden Stardust',
            '✨ Floating Diya & Auspicious Mandala Reveal Trailer',
            '🥂 Sangeet Dance Floor Glamour Motion Card'
        ],
        song: [
            '🪕 Mangal Bela: Auspicious Shehnai & Sitar Theme Song',
            '🥁 Royal Dholak & Flute Sangeet Celebration Melody',
            '💖 Soulful Acoustic Sufi Wedding Theme & Lyrics'
        ]
    };

    return (
        <div className="gemini-ai-studio-container" style={{
            background: isLightTheme ? '#FFFFFF' : 'rgba(15, 23, 42, 0.95)',
            border: isLightTheme ? '1.5px solid #E2E8F0' : '1px solid rgba(139, 92, 246, 0.35)',
            borderRadius: '24px',
            overflow: 'hidden',
            boxShadow: isLightTheme ? '0 12px 35px rgba(0, 0, 0, 0.06)' : '0 18px 45px rgba(0, 0, 0, 0.45)',
            display: 'flex',
            flexDirection: 'column',
            marginBottom: '2.5rem'
        }}>
            {/* ============================================================== */}
            {/* 1. TOP HEADER: Nano Banana Mascot + Context Pill + Quota       */}
            {/* ============================================================== */}
            <div style={{
                padding: '1rem 1.35rem',
                borderBottom: isLightTheme ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '12px',
                background: isLightTheme ? 'linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 100%)' : 'rgba(30, 41, 59, 0.6)'
            }}>
                {/* Left: Mascot & Brand */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                        width: '42px',
                        height: '42px',
                        borderRadius: '14px',
                        background: 'linear-gradient(135deg, #FEF3C7 0%, #F59E0B 50%, #EC4899 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.5rem',
                        boxShadow: '0 4px 14px rgba(245, 158, 11, 0.35)'
                    }}>
                        🍌
                    </div>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 800, fontSize: '1.15rem', color: isLightTheme ? '#0F172A' : '#F8FAFC', fontFamily: "'Outfit', sans-serif" }}>
                                Nano Banana Pro 🍌⚡
                            </span>
                            <span style={{
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: '0.68rem',
                                fontWeight: 800,
                                background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                                color: '#FFFFFF',
                                letterSpacing: '0.04em'
                            }}>
                                GEMINI MULTIMODAL PRO
                            </span>
                        </div>
                        <div style={{ fontSize: '0.78rem', color: isLightTheme ? '#64748B' : '#94A3B8' }}>
                            Next-gen AI Image Posters, Cinematic Videos, Full Wedding Songs & Multilingual Scripts
                        </div>
                    </div>
                </div>

                {/* Center / Right: Event Context Pill & Quota Badge */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    {/* Event Context Pill */}
                    <div style={{
                        padding: '4px 10px',
                        borderRadius: '20px',
                        fontSize: '0.74rem',
                        fontWeight: 600,
                        background: isLightTheme ? '#F1F5F9' : 'rgba(255, 255, 255, 0.06)',
                        color: isLightTheme ? '#475569' : '#CBD5E1',
                        border: isLightTheme ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                    }}>
                        <span>🎉</span>
                        <span style={{ maxWidth: '240px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {eventName || 'Our Celebration'} • {formattedDate}
                        </span>
                    </div>

                    {/* Quota Badge */}
                    <div style={{
                        padding: '4px 10px',
                        borderRadius: '20px',
                        fontSize: '0.74rem',
                        fontWeight: 800,
                        background: hasAiPlan ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)' : (isLightTheme ? '#FEF3C7' : 'rgba(245, 158, 11, 0.18)'),
                        color: hasAiPlan ? '#FFFFFF' : '#D97706',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                    }}>
                        <span>{hasAiPlan ? '👑' : '🍌'}</span>
                        <span>{hasAiPlan ? 'UNLIMITED AI' : `${remainingGenerations}/5 FREE`}</span>
                    </div>

                    {/* Upgrade button if not on AI Plan */}
                    {!hasAiPlan && (
                        <button
                            type="button"
                            onClick={() => setIsUpgradeModalOpen(true)}
                            style={{
                                padding: '4px 10px',
                                borderRadius: '20px',
                                border: 'none',
                                background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                                color: '#FFFFFF',
                                fontSize: '0.72rem',
                                fontWeight: 800,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)'
                            }}
                        >
                            <Zap size={12} /> UPGRADE
                        </button>
                    )}

                    {/* Reset Chat */}
                    <button
                        type="button"
                        onClick={handleResetChat}
                        title="Clear conversation"
                        style={{
                            padding: '6px',
                            borderRadius: '50%',
                            border: isLightTheme ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.1)',
                            background: 'transparent',
                            color: isLightTheme ? '#94A3B8' : '#64748B',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <RotateCcw size={14} />
                    </button>
                </div>
            </div>

            {/* ============================================================== */}
            {/* 2. CHAT STREAM / MESSAGE THREAD (Gemini / ChatGPT Style)        */}
            {/* ============================================================== */}
            <div className="gemini-chat-stream" style={{
                flex: 1,
                minHeight: '380px',
                maxHeight: '560px',
                overflowY: 'auto',
                padding: '1.25rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '1.25rem',
                background: isLightTheme ? '#FAFAFA' : 'rgba(15, 23, 42, 0.4)'
            }}>
                {messages.map((msg) => {
                    const isUser = msg.role === 'user';
                    return (
                        <div
                            key={msg.id}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: isUser ? 'flex-end' : 'flex-start',
                                maxWidth: '100%'
                            }}
                        >
                            {/* Message Capsule */}
                            <div style={{
                                display: 'flex',
                                gap: '10px',
                                maxWidth: isUser ? '85%' : '92%',
                                flexDirection: isUser ? 'row-reverse' : 'row'
                            }}>
                                {/* Avatar */}
                                <div style={{
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '50%',
                                    background: isUser
                                        ? 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)'
                                        : 'linear-gradient(135deg, #FEF3C7 0%, #F59E0B 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: isUser ? '0.85rem' : '1.1rem',
                                    color: '#FFFFFF',
                                    flexShrink: 0,
                                    marginTop: '2px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                }}>
                                    {isUser ? '👤' : '🍌'}
                                </div>

                                {/* Content Box */}
                                <div style={{
                                    background: isUser
                                        ? (isLightTheme ? '#8B5CF6' : '#7C3AED')
                                        : (isLightTheme ? '#FFFFFF' : '#1E293B'),
                                    color: isUser
                                        ? '#FFFFFF'
                                        : (isLightTheme ? '#0F172A' : '#F8FAFC'),
                                    padding: '0.85rem 1.15rem',
                                    borderRadius: isUser ? '18px 4px 18px 18px' : '4px 18px 18px 18px',
                                    border: isUser
                                        ? 'none'
                                        : (isLightTheme ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)'),
                                    boxShadow: isUser ? '0 4px 14px rgba(139, 92, 246, 0.25)' : '0 4px 16px rgba(0, 0, 0, 0.06)',
                                    fontSize: '0.92rem',
                                    lineHeight: 1.55,
                                    width: '100%'
                                }}>
                                    {/* User Prompt Bubble */}
                                    {isUser ? (
                                        <div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                                <span style={{
                                                    fontSize: '0.68rem',
                                                    fontWeight: 800,
                                                    padding: '2px 6px',
                                                    borderRadius: '8px',
                                                    background: 'rgba(255,255,255,0.25)',
                                                    textTransform: 'uppercase'
                                                }}>
                                                    {msg.mode}
                                                </span>
                                            </div>
                                            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                {msg.prompt}
                                            </div>
                                        </div>
                                    ) : (
                                        /* Assistant Response Card */
                                        <div>
                                            {/* Header Tag */}
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                marginBottom: '8px',
                                                paddingBottom: '6px',
                                                borderBottom: isLightTheme ? '1px solid #F1F5F9' : '1px solid rgba(255, 255, 255, 0.06)'
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <span style={{ fontSize: '0.85rem' }}>
                                                        {msg.mode === 'text' ? '✍️' : msg.mode === 'image' ? '🖼️' : msg.mode === 'video' ? '🎬' : '🎵'}
                                                    </span>
                                                    <span style={{ fontSize: '0.78rem', fontWeight: 800, color: '#F59E0B', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                        {msg.mode === 'text' ? 'Invitation Wording' : msg.mode === 'image' ? 'Ultra-HD Invitation Poster' : msg.mode === 'video' ? 'Cinematic Motion Teaser' : 'Celebratory Song & Theme'}
                                                    </span>
                                                </div>
                                                <span style={{ fontSize: '0.7rem', color: isLightTheme ? '#94A3B8' : '#64748B' }}>
                                                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </span>
                                            </div>

                                            {/* 1. TEXT CONTENT */}
                                            {msg.mode === 'text' && msg.content && (
                                                <div>
                                                    <div style={{
                                                        background: isLightTheme ? '#F8FAFC' : 'rgba(15, 23, 42, 0.6)',
                                                        border: isLightTheme ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.06)',
                                                        borderRadius: '12px',
                                                        padding: '0.85rem',
                                                        fontFamily: 'inherit',
                                                        fontSize: '0.88rem',
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word',
                                                        lineHeight: 1.6,
                                                        color: isLightTheme ? '#1E293B' : '#E2E8F0',
                                                        marginBottom: '10px'
                                                    }}>
                                                        {msg.content}
                                                    </div>

                                                    {/* Action Buttons for Text */}
                                                    {msg.id !== 'init-welcome' && (
                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '6px' }}>
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    onApplyTemplate(msg.content || '');
                                                                    showToast('✅ Applied AI wording to your Step 2 Invitation!', 'success');
                                                                }}
                                                                style={{
                                                                    padding: '7px 14px',
                                                                    borderRadius: '8px',
                                                                    border: 'none',
                                                                    background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                                                                    color: '#FFFFFF',
                                                                    fontWeight: 700,
                                                                    fontSize: '0.8rem',
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '6px',
                                                                    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                                                                }}
                                                            >
                                                                <Check size={14} /> Apply to Invitation
                                                            </button>

                                                            <button
                                                                type="button"
                                                                onClick={() => handleCopyText(msg.content || '')}
                                                                style={{
                                                                    padding: '7px 12px',
                                                                    borderRadius: '8px',
                                                                    border: isLightTheme ? '1px solid #CBD5E1' : '1px solid rgba(255, 255, 255, 0.15)',
                                                                    background: isLightTheme ? '#F1F5F9' : 'rgba(255, 255, 255, 0.06)',
                                                                    color: isLightTheme ? '#334155' : '#E2E8F0',
                                                                    fontWeight: 600,
                                                                    fontSize: '0.8rem',
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '6px'
                                                                }}
                                                            >
                                                                <Copy size={13} /> Copy
                                                            </button>

                                                            <button
                                                                type="button"
                                                                onClick={() => handleGenerate(msg.prompt, 'text', true, msg.id)}
                                                                disabled={isGenerating}
                                                                style={{
                                                                    padding: '7px 12px',
                                                                    borderRadius: '8px',
                                                                    border: isLightTheme ? '1px solid #CBD5E1' : '1px solid rgba(255, 255, 255, 0.15)',
                                                                    background: isLightTheme ? '#F1F5F9' : 'rgba(255, 255, 255, 0.06)',
                                                                    color: isLightTheme ? '#334155' : '#E2E8F0',
                                                                    fontWeight: 600,
                                                                    fontSize: '0.8rem',
                                                                    cursor: isGenerating ? 'not-allowed' : 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '6px'
                                                                }}
                                                            >
                                                                <RefreshCw size={13} className={isGenerating ? 'spin' : ''} /> Regenerate
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* 2. IMAGE CONTENT */}
                                            {msg.mode === 'image' && msg.mediaUrl && (
                                                <div>
                                                    <div style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '5px',
                                                        fontSize: '0.72rem',
                                                        fontWeight: 700,
                                                        color: '#F59E0B',
                                                        background: isLightTheme ? '#FFFBEB' : 'rgba(245, 158, 11, 0.12)',
                                                        border: '1px solid rgba(245, 158, 11, 0.3)',
                                                        padding: '3px 10px',
                                                        borderRadius: '12px',
                                                        marginBottom: '8px'
                                                    }}>
                                                        <span>🍌⚡</span>
                                                        <span>{msg.modelUsed || 'Nano Banana Pro (Gemini 3 Pro Image)'}</span>
                                                    </div>

                                                    <div style={{
                                                        borderRadius: '14px',
                                                        overflow: 'hidden',
                                                        border: isLightTheme ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.1)',
                                                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                                                        marginBottom: '10px',
                                                        maxWidth: '380px',
                                                        position: 'relative'
                                                    }}>
                                                        <img
                                                            src={msg.mediaUrl}
                                                            alt="Generated Invitation Poster"
                                                            style={{
                                                                width: '100%',
                                                                height: 'auto',
                                                                display: 'block',
                                                                aspectRatio: '1/1',
                                                                objectFit: 'cover'
                                                            }}
                                                        />
                                                    </div>

                                                    {/* Action Buttons for Image */}
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '6px' }}>
                                                        {msg.file && (
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    onAttachMedia(msg.file!);
                                                                    showToast('🪄 Attached AI Poster to your WhatsApp invitation!', 'success');
                                                                }}
                                                                disabled={isUploadingMedia}
                                                                style={{
                                                                    padding: '7px 14px',
                                                                    borderRadius: '8px',
                                                                    border: 'none',
                                                                    background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                                                                    color: '#FFFFFF',
                                                                    fontWeight: 700,
                                                                    fontSize: '0.8rem',
                                                                    cursor: isUploadingMedia ? 'not-allowed' : 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '6px',
                                                                    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                                                                }}
                                                            >
                                                                <Wand2 size={14} /> Attach to WhatsApp
                                                            </button>
                                                        )}

                                                        <button
                                                            type="button"
                                                            onClick={() => handleDownloadMedia(msg.mediaUrl!, 'invitation-poster.jpg')}
                                                            style={{
                                                                padding: '7px 12px',
                                                                borderRadius: '8px',
                                                                border: isLightTheme ? '1px solid #CBD5E1' : '1px solid rgba(255, 255, 255, 0.15)',
                                                                background: isLightTheme ? '#F1F5F9' : 'rgba(255, 255, 255, 0.06)',
                                                                color: isLightTheme ? '#334155' : '#E2E8F0',
                                                                fontWeight: 600,
                                                                fontSize: '0.8rem',
                                                                cursor: 'pointer',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '6px'
                                                            }}
                                                        >
                                                            <Download size={13} /> Download Poster
                                                        </button>

                                                        <button
                                                            type="button"
                                                            onClick={() => handleGenerate(msg.prompt, 'image', true, msg.id)}
                                                            disabled={isGenerating}
                                                            style={{
                                                                padding: '7px 12px',
                                                                borderRadius: '8px',
                                                                border: isLightTheme ? '1px solid #CBD5E1' : '1px solid rgba(255, 255, 255, 0.15)',
                                                                background: isLightTheme ? '#F1F5F9' : 'rgba(255, 255, 255, 0.06)',
                                                                color: isLightTheme ? '#334155' : '#E2E8F0',
                                                                fontWeight: 600,
                                                                fontSize: '0.8rem',
                                                                cursor: isGenerating ? 'not-allowed' : 'pointer',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '6px'
                                                            }}
                                                        >
                                                            <RefreshCw size={13} className={isGenerating ? 'spin' : ''} /> Regenerate
                                                        </button>
                                                    </div>
                                                </div>
                                            )}

                                            {/* 3. VIDEO CONTENT */}
                                            {msg.mode === 'video' && msg.mediaUrl && (
                                                <div>
                                                    <div style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '5px',
                                                        fontSize: '0.72rem',
                                                        fontWeight: 700,
                                                        color: '#3B82F6',
                                                        background: isLightTheme ? '#EFF6FF' : 'rgba(59, 130, 246, 0.12)',
                                                        border: '1px solid rgba(59, 130, 246, 0.3)',
                                                        padding: '3px 10px',
                                                        borderRadius: '12px',
                                                        marginBottom: '8px'
                                                    }}>
                                                        <span>🎬⚡</span>
                                                        <span>{msg.modelUsed || 'Nano Banana Pro Cinematic Engine'}</span>
                                                    </div>

                                                    <div style={{
                                                        borderRadius: '14px',
                                                        overflow: 'hidden',
                                                        border: isLightTheme ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.1)',
                                                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
                                                        marginBottom: '10px',
                                                        maxWidth: '380px',
                                                        background: '#000000'
                                                    }}>
                                                        <video
                                                            src={msg.mediaUrl}
                                                            controls
                                                            autoPlay
                                                            loop
                                                            muted
                                                            playsInline
                                                            style={{
                                                                width: '100%',
                                                                height: 'auto',
                                                                display: 'block',
                                                                aspectRatio: '1/1'
                                                            }}
                                                        />
                                                    </div>

                                                    {/* Storyboard display if available */}
                                                    {msg.storyboard && msg.storyboard.length > 0 && (
                                                        <div style={{
                                                            maxWidth: '380px',
                                                            padding: '10px 12px',
                                                            borderRadius: '12px',
                                                            background: isLightTheme ? '#F8FAFC' : 'rgba(255, 255, 255, 0.04)',
                                                            border: isLightTheme ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)',
                                                            marginBottom: '10px',
                                                            fontSize: '0.75rem'
                                                        }}>
                                                            <div style={{ fontWeight: 800, color: '#3B82F6', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                <VideoIcon size={12} /> Cinematic Storyboard (Nano Banana Pro)
                                                            </div>
                                                            {msg.storyboard.map((sc, i) => (
                                                                <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '4px', color: isLightTheme ? '#475569' : '#CBD5E1' }}>
                                                                    <span style={{ fontWeight: 700, color: '#F59E0B', flexShrink: 0 }}>S{sc.scene} ({sc.duration}):</span>
                                                                    <span>{sc.description}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {/* Action Buttons for Video */}
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '6px' }}>
                                                        {msg.file && (
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    onAttachMedia(msg.file!);
                                                                    showToast('🪄 Attached AI Video Teaser to your WhatsApp invitation!', 'success');
                                                                }}
                                                                disabled={isUploadingMedia}
                                                                style={{
                                                                    padding: '7px 14px',
                                                                    borderRadius: '8px',
                                                                    border: 'none',
                                                                    background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                                                                    color: '#FFFFFF',
                                                                    fontWeight: 700,
                                                                    fontSize: '0.8rem',
                                                                    cursor: isUploadingMedia ? 'not-allowed' : 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '6px',
                                                                    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                                                                }}
                                                            >
                                                                <Wand2 size={14} /> Attach as Video
                                                            </button>
                                                        )}

                                                        <button
                                                            type="button"
                                                            onClick={() => handleDownloadMedia(msg.mediaUrl!, 'invitation-teaser.mp4')}
                                                            style={{
                                                                padding: '7px 12px',
                                                                borderRadius: '8px',
                                                                border: isLightTheme ? '1px solid #CBD5E1' : '1px solid rgba(255, 255, 255, 0.15)',
                                                                background: isLightTheme ? '#F1F5F9' : 'rgba(255, 255, 255, 0.06)',
                                                                color: isLightTheme ? '#334155' : '#E2E8F0',
                                                                fontWeight: 600,
                                                                fontSize: '0.8rem',
                                                                cursor: 'pointer',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '6px'
                                                            }}
                                                        >
                                                            <Download size={13} /> Download Video
                                                        </button>

                                                        <button
                                                            type="button"
                                                            onClick={() => handleGenerate(msg.prompt, 'video', true, msg.id)}
                                                            disabled={isGenerating}
                                                            style={{
                                                                padding: '7px 12px',
                                                                borderRadius: '8px',
                                                                border: isLightTheme ? '1px solid #CBD5E1' : '1px solid rgba(255, 255, 255, 0.15)',
                                                                background: isLightTheme ? '#F1F5F9' : 'rgba(255, 255, 255, 0.06)',
                                                                color: isLightTheme ? '#334155' : '#E2E8F0',
                                                                fontWeight: 600,
                                                                fontSize: '0.8rem',
                                                                cursor: isGenerating ? 'not-allowed' : 'pointer',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '6px'
                                                            }}
                                                        >
                                                            <RefreshCw size={13} className={isGenerating ? 'spin' : ''} /> Regenerate
                                                        </button>
                                                    </div>
                                                </div>
                                            )}

                                            {/* 4. SONG CONTENT */}
                                            {msg.mode === 'song' && msg.audioUrl && (
                                                <div>
                                                    <div style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '5px',
                                                        fontSize: '0.72rem',
                                                        fontWeight: 700,
                                                        color: '#10B981',
                                                        background: isLightTheme ? '#ECFDF5' : 'rgba(16, 185, 129, 0.12)',
                                                        border: '1px solid rgba(16, 185, 129, 0.3)',
                                                        padding: '3px 10px',
                                                        borderRadius: '12px',
                                                        marginBottom: '8px'
                                                    }}>
                                                        <span>🎵⚡</span>
                                                        <span>{msg.modelUsed || 'Nano Banana Pro Audio Maestro'}</span>
                                                    </div>

                                                    <div style={{
                                                        background: isLightTheme ? '#F8FAFC' : 'rgba(15, 23, 42, 0.7)',
                                                        border: isLightTheme ? '1.5px solid #E2E8F0' : '1px solid rgba(245, 158, 11, 0.3)',
                                                        borderRadius: '16px',
                                                        padding: '1rem',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '14px',
                                                        marginBottom: '10px',
                                                        maxWidth: '440px'
                                                    }}>
                                                        {/* Play / Pause Button */}
                                                        <button
                                                            type="button"
                                                            onClick={() => handleToggleAudio(msg.audioUrl!, msg.id)}
                                                            style={{
                                                                width: '46px',
                                                                height: '46px',
                                                                borderRadius: '50%',
                                                                border: 'none',
                                                                background: activeAudioPlayingId === msg.id
                                                                    ? 'linear-gradient(135deg, #EC4899 0%, #8B5CF6 100%)'
                                                                    : 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                                                                color: '#FFFFFF',
                                                                cursor: 'pointer',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                flexShrink: 0,
                                                                boxShadow: '0 4px 14px rgba(245, 158, 11, 0.4)'
                                                            }}
                                                        >
                                                            {activeAudioPlayingId === msg.id ? <Pause size={20} /> : <Play size={20} style={{ marginLeft: '2px' }} />}
                                                        </button>

                                                        {/* Track Info & Animated Waveform */}
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <div style={{ fontWeight: 800, fontSize: '0.88rem', color: isLightTheme ? '#0F172A' : '#F8FAFC', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                {msg.audioTitle || 'Celebratory Wedding Theme'}
                                                            </div>
                                                            <div style={{ fontSize: '0.74rem', color: isLightTheme ? '#64748B' : '#94A3B8', marginBottom: '6px' }}>
                                                                {msg.genre || 'Auspicious Shehnai & Sitar'} • 0:{msg.audioDuration || 14}
                                                            </div>

                                                            {/* Audio Waveform Bars */}
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '3px', height: '22px' }}>
                                                                {Array.from({ length: 24 }).map((_, i) => {
                                                                    const isPlaying = activeAudioPlayingId === msg.id;
                                                                    const h = isPlaying
                                                                        ? Math.sin(i * 0.7 + Date.now() * 0.005) * 8 + 12
                                                                        : (i % 3 === 0 ? 14 : i % 2 === 0 ? 8 : 18);
                                                                    return (
                                                                        <div
                                                                            key={i}
                                                                            style={{
                                                                                width: '3px',
                                                                                height: `${h}px`,
                                                                                borderRadius: '2px',
                                                                                background: isPlaying ? '#EC4899' : (isLightTheme ? '#CBD5E1' : 'rgba(255, 255, 255, 0.3)'),
                                                                                transition: 'height 0.15s ease'
                                                                            }}
                                                                        />
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* AI Composed Lyrics Box */}
                                                    {msg.lyrics && (
                                                        <div style={{
                                                            maxWidth: '440px',
                                                            marginTop: '10px',
                                                            marginBottom: '10px',
                                                            padding: '12px 14px',
                                                            borderRadius: '14px',
                                                            background: isLightTheme ? '#FFFFFF' : 'rgba(255, 255, 255, 0.04)',
                                                            border: isLightTheme ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)',
                                                            fontSize: '0.82rem',
                                                            lineHeight: '1.6'
                                                        }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                                                                <span style={{ fontWeight: 800, color: '#F59E0B', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                    <Music size={14} /> AI Composed Lyrics (Nano Banana Pro)
                                                                </span>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        const fullLyrics = [msg.lyrics?.mukhda, msg.lyrics?.antra1, msg.lyrics?.antra2, msg.lyrics?.outro].filter(Boolean).join('\n\n');
                                                                        navigator.clipboard.writeText(fullLyrics);
                                                                        showToast('Copied full song lyrics to clipboard!', 'success');
                                                                    }}
                                                                    style={{
                                                                        background: 'transparent',
                                                                        border: 'none',
                                                                        color: isLightTheme ? '#64748B' : '#94A3B8',
                                                                        cursor: 'pointer',
                                                                        fontSize: '0.74rem',
                                                                        fontWeight: 600,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: '4px'
                                                                    }}
                                                                >
                                                                    <Copy size={12} /> Copy Lyrics
                                                                </button>
                                                            </div>
                                                            {msg.lyrics.mukhda && (
                                                                <div style={{ marginBottom: '8px', whiteSpace: 'pre-line', color: isLightTheme ? '#1E293B' : '#E2E8F0', fontWeight: 600 }}>
                                                                    {msg.lyrics.mukhda}
                                                                </div>
                                                            )}
                                                            {msg.lyrics.antra1 && (
                                                                <div style={{ marginBottom: '8px', whiteSpace: 'pre-line', color: isLightTheme ? '#334155' : '#CBD5E1' }}>
                                                                    {msg.lyrics.antra1}
                                                                </div>
                                                            )}
                                                            {msg.lyrics.antra2 && (
                                                                <div style={{ marginBottom: '8px', whiteSpace: 'pre-line', color: isLightTheme ? '#334155' : '#CBD5E1' }}>
                                                                    {msg.lyrics.antra2}
                                                                </div>
                                                            )}
                                                            {msg.lyrics.outro && (
                                                                <div style={{ whiteSpace: 'pre-line', fontStyle: 'italic', color: isLightTheme ? '#475569' : '#94A3B8' }}>
                                                                    {msg.lyrics.outro}
                                                                </div>
                                                            )}
                                                            {msg.suggestedInstruments && msg.suggestedInstruments.length > 0 && (
                                                                <div style={{ marginTop: '10px', paddingTop: '8px', borderTop: isLightTheme ? '1px dashed #E2E8F0' : '1px dashed rgba(255, 255, 255, 0.1)', display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                                                                    <span style={{ fontSize: '0.72rem', color: isLightTheme ? '#94A3B8' : '#64748B', fontWeight: 700 }}>Instruments:</span>
                                                                    {msg.suggestedInstruments.map((inst, idx) => (
                                                                        <span key={idx} style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: '10px', background: isLightTheme ? '#F1F5F9' : 'rgba(255, 255, 255, 0.08)', color: isLightTheme ? '#475569' : '#CBD5E1' }}>
                                                                            🪕 {inst}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Action Buttons for Song */}
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '6px' }}>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDownloadMedia(msg.audioUrl!, 'invitation-song.wav')}
                                                            style={{
                                                                padding: '7px 14px',
                                                                borderRadius: '8px',
                                                                border: 'none',
                                                                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                                                                color: '#FFFFFF',
                                                                fontWeight: 700,
                                                                fontSize: '0.8rem',
                                                                cursor: 'pointer',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '6px',
                                                                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                                                            }}
                                                        >
                                                            <Download size={13} /> Download Song (WAV)
                                                        </button>

                                                        <button
                                                            type="button"
                                                            onClick={() => handleGenerate(msg.prompt, 'song', true, msg.id)}
                                                            disabled={isGenerating}
                                                            style={{
                                                                padding: '7px 12px',
                                                                borderRadius: '8px',
                                                                border: isLightTheme ? '1px solid #CBD5E1' : '1px solid rgba(255, 255, 255, 0.15)',
                                                                background: isLightTheme ? '#F1F5F9' : 'rgba(255, 255, 255, 0.06)',
                                                                color: isLightTheme ? '#334155' : '#E2E8F0',
                                                                fontWeight: 600,
                                                                fontSize: '0.8rem',
                                                                cursor: isGenerating ? 'not-allowed' : 'pointer',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '6px'
                                                            }}
                                                        >
                                                            <RefreshCw size={13} className={isGenerating ? 'spin' : ''} /> Regenerate
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                            {/* AI Prompt Optimization Banner */}
                                            {msg.optimizedPrompt && (
                                                <div style={{
                                                    marginTop: '12px',
                                                    padding: '10px 14px',
                                                    borderRadius: '12px',
                                                    background: isLightTheme ? '#FFFBEB' : 'rgba(245, 158, 11, 0.08)',
                                                    border: isLightTheme ? '1px solid #FDE68A' : '1px solid rgba(245, 158, 11, 0.25)',
                                                    fontSize: '0.76rem'
                                                }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                                                        <span style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#D97706', fontWeight: 800 }}>
                                                            <Sparkles size={13} /> AI Prompt Optimization Applied
                                                        </span>
                                                        <span style={{ fontSize: '0.68rem', opacity: 0.7, color: isLightTheme ? '#92400E' : '#FDE68A' }}>
                                                            Polished before LLM synthesis
                                                        </span>
                                                    </div>
                                                    <div style={{ color: isLightTheme ? '#78350F' : '#FDE68A', opacity: 0.95, lineHeight: 1.45, fontStyle: 'italic' }}>
                                                        "{msg.optimizedPrompt}"
                                                    </div>
                                                    {msg.enhancementNotes && msg.enhancementNotes.length > 0 && (
                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>
                                                            {msg.enhancementNotes.map((note, idx) => (
                                                                <span key={idx} style={{
                                                                    background: isLightTheme ? '#FEF3C7' : 'rgba(245, 158, 11, 0.18)',
                                                                    color: isLightTheme ? '#92400E' : '#FCD34D',
                                                                    padding: '2px 8px',
                                                                    borderRadius: '6px',
                                                                    fontSize: '0.68rem',
                                                                    fontWeight: 600
                                                                }}>
                                                                    ✓ {note}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}

                {/* Loading / Generating Indicator */}
                {isGenerating && (
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #FEF3C7 0%, #F59E0B 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1.1rem',
                            flexShrink: 0
                        }}>
                            🍌
                        </div>
                        <div style={{
                            background: isLightTheme ? '#FFFFFF' : '#1E293B',
                            padding: '0.75rem 1.15rem',
                            borderRadius: '16px',
                            border: isLightTheme ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)',
                            fontSize: '0.86rem',
                            color: isLightTheme ? '#475569' : '#CBD5E1',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px'
                        }}>
                            <RefreshCw size={14} className="spin" color="#F59E0B" />
                            <span>Nano Banana Pro 🍌⚡ is synthesizing your <strong>{selectedMode.toUpperCase()}</strong> with Gemini AI...</span>
                        </div>
                    </div>
                )}

                <div ref={chatEndRef} />
            </div>

            {/* ============================================================== */}
            {/* 3. PROMPT STARTERS (Chips for active mode)                     */}
            {/* ============================================================== */}
            <div style={{
                padding: '0.6rem 1.25rem',
                background: isLightTheme ? '#F8FAFC' : 'rgba(15, 23, 42, 0.7)',
                borderTop: isLightTheme ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.06)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch'
            }}>
                <span style={{ fontSize: '0.74rem', fontWeight: 700, color: isLightTheme ? '#94A3B8' : '#64748B', whiteSpace: 'nowrap' }}>
                    💡 Suggestions:
                </span>
                {promptStarters[selectedMode].map((starter, idx) => (
                    <button
                        key={idx}
                        type="button"
                        onClick={() => handleGenerate(starter, selectedMode)}
                        disabled={isGenerating}
                        style={{
                            padding: '4px 10px',
                            borderRadius: '14px',
                            border: isLightTheme ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.1)',
                            background: isLightTheme ? '#FFFFFF' : 'rgba(255, 255, 255, 0.04)',
                            color: isLightTheme ? '#475569' : '#CBD5E1',
                            fontSize: '0.74rem',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'all 0.15s ease'
                        }}
                    >
                        <span>✨</span>
                        <span>{starter}</span>
                    </button>
                ))}
            </div>

            {/* ============================================================== */}
            {/* 4. PINNED PROMPT BAR (Gemini / ChatGPT Style)                   */}
            {/* ============================================================== */}
            <div style={{
                padding: '1rem 1.25rem',
                borderTop: isLightTheme ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)',
                background: isLightTheme ? '#FFFFFF' : '#0F172A',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
            }}>
                {/* Mode Selector Tabs (Text, Image, Video, Song) */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    flexWrap: 'wrap'
                }}>
                    <span style={{ fontSize: '0.76rem', fontWeight: 800, color: isLightTheme ? '#64748B' : '#94A3B8', textTransform: 'uppercase', marginRight: '4px' }}>
                        Generate:
                    </span>

                    {(['text', 'image', 'video', 'song'] as GenAiMode[]).map((mode) => {
                        const isSelected = selectedMode === mode;
                        const icon = mode === 'text' ? '✍️' : mode === 'image' ? '🖼️' : mode === 'video' ? '🎬' : '🎵';
                        const label = mode === 'text' ? 'Scripts' : mode === 'image' ? 'Image Poster' : mode === 'video' ? 'Video Teaser' : 'Wedding Song';

                        return (
                            <button
                                key={mode}
                                type="button"
                                onClick={() => setSelectedMode(mode)}
                                style={{
                                    padding: '6px 14px',
                                    borderRadius: '20px',
                                    border: isSelected
                                        ? 'none'
                                        : (isLightTheme ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.1)'),
                                    background: isSelected
                                        ? (mode === 'text' ? 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)'
                                            : mode === 'image' ? 'linear-gradient(135deg, #EC4899 0%, #F59E0B 100%)'
                                            : mode === 'video' ? 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)'
                                            : 'linear-gradient(135deg, #10B981 0%, #059669 100%)')
                                        : (isLightTheme ? '#F8FAFC' : 'rgba(255, 255, 255, 0.04)'),
                                    color: isSelected ? '#FFFFFF' : (isLightTheme ? '#475569' : '#CBD5E1'),
                                    fontWeight: 700,
                                    fontSize: '0.8rem',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    boxShadow: isSelected ? '0 2px 10px rgba(0,0,0,0.15)' : 'none',
                                    transition: 'all 0.2s'
                                }}
                            >
                                <span>{icon}</span>
                                <span>{label}</span>
                            </button>
                        );
                    })}
                </div>

                {/* Input Capsule Box */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: isLightTheme ? '#F1F5F9' : 'rgba(255, 255, 255, 0.06)',
                    borderRadius: '16px',
                    padding: '6px 8px 6px 14px',
                    border: isLightTheme ? '1.5px solid #CBD5E1' : '1px solid rgba(255, 255, 255, 0.12)'
                }}>
                    <textarea
                        rows={1}
                        value={inputPrompt}
                        onChange={(e) => setInputPrompt(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleGenerate();
                            }
                        }}
                        placeholder={
                            selectedMode === 'text'
                                ? `Ask Nano Banana Pro to write invitation scripts (e.g. royal Sanskrit tone, emotional family blessing, romantic poetry)...`
                                : selectedMode === 'image'
                                ? `Describe your invitation card style (e.g. royal palace 4k card with golden mandap, embossed foil)...`
                                : selectedMode === 'video'
                                ? `Describe your video teaser (e.g. 10s cinematic trailer with rotating mandala & couple names)...`
                                : `Describe your festive song vibe (e.g. auspicious shehnai & sitar theme, upbeat sangeet dholak)...`
                        }
                        style={{
                            flex: 1,
                            background: 'transparent',
                            border: 'none',
                            outline: 'none',
                            resize: 'none',
                            fontSize: '0.92rem',
                            color: isLightTheme ? '#0F172A' : '#F8FAFC',
                            fontFamily: 'inherit',
                            maxHeight: '120px'
                        }}
                    />

                    {/* Prompt Optimizer Button */}
                    <button
                        type="button"
                        onClick={handleOptimizePrompt}
                        disabled={isGenerating || isOptimizingPrompt || !inputPrompt.trim()}
                        title="Optimize and enrich prompt with luxury wedding aesthetics, lighting, and cultural motifs before sending"
                        style={{
                            padding: '8px 12px',
                            borderRadius: '10px',
                            border: isLightTheme ? '1px solid #FCD34D' : '1px solid rgba(245, 158, 11, 0.4)',
                            background: !inputPrompt.trim()
                                ? (isLightTheme ? '#F8FAFC' : 'rgba(255, 255, 255, 0.04)')
                                : (isLightTheme ? '#FFFBEB' : 'rgba(245, 158, 11, 0.12)'),
                            color: !inputPrompt.trim()
                                ? (isLightTheme ? '#94A3B8' : '#64748B')
                                : '#D97706',
                            fontWeight: 700,
                            fontSize: '0.8rem',
                            cursor: isGenerating || isOptimizingPrompt || !inputPrompt.trim() ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                            flexShrink: 0,
                            transition: 'all 0.2s',
                            boxShadow: inputPrompt.trim() ? '0 2px 6px rgba(245, 158, 11, 0.15)' : 'none'
                        }}
                    >
                        {isOptimizingPrompt ? <RefreshCw size={14} className="spin" /> : <Wand2 size={14} />}
                        <span style={{ display: 'inline-block' }}>{isOptimizingPrompt ? 'Polishing...' : '✨ Polish Prompt'}</span>
                    </button>

                    {/* Send Button */}
                    <button
                        type="button"
                        onClick={() => handleGenerate()}
                        disabled={isGenerating || !inputPrompt.trim()}
                        style={{
                            padding: '10px 18px',
                            borderRadius: '12px',
                            border: 'none',
                            background: !inputPrompt.trim() || isGenerating
                                ? (isLightTheme ? '#E2E8F0' : 'rgba(255, 255, 255, 0.1)')
                                : 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                            color: !inputPrompt.trim() || isGenerating ? '#94A3B8' : '#FFFFFF',
                            fontWeight: 800,
                            fontSize: '0.86rem',
                            cursor: !inputPrompt.trim() || isGenerating ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            boxShadow: !inputPrompt.trim() || isGenerating ? 'none' : '0 4px 14px rgba(245, 158, 11, 0.4)',
                            transition: 'all 0.2s',
                            flexShrink: 0
                        }}
                    >
                        {isGenerating ? <RefreshCw size={16} className="spin" /> : <Sparkles size={16} />}
                        <span>Generate</span>
                    </button>
                </div>

                {/* Subtext info */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '0.72rem',
                    color: isLightTheme ? '#94A3B8' : '#64748B',
                    padding: '0 4px'
                }}>
                    <span>Personalized for {eventName || 'your celebration'} ({formattedDate})</span>
                    <span>Press Enter to send • Powered by Nano Banana Pro 🍌⚡</span>
                </div>
            </div>

            {/* ============================================================== */}
            {/* 5. NANO BANANA AI UPGRADE MODAL                                */}
            {/* ============================================================== */}
            {isUpgradeModalOpen && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(15, 23, 42, 0.8)',
                    backdropFilter: 'blur(8px)',
                    zIndex: 99999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '1rem'
                }}>
                    <div className="ai-upgrade-modal-card" style={{
                        background: isLightTheme ? '#FFFFFF' : '#0F172A',
                        border: '2px solid #F59E0B',
                        borderRadius: '24px',
                        width: '100%',
                        maxWidth: '520px',
                        padding: 'clamp(1.25rem, 4vw, 2rem)',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        position: 'relative',
                        boxShadow: '0 20px 60px rgba(245, 158, 11, 0.35)',
                        color: isLightTheme ? '#0F172A' : '#F8FAFC'
                    }}>
                        {/* Close Button */}
                        <button
                            type="button"
                            onClick={() => setIsUpgradeModalOpen(false)}
                            style={{
                                position: 'absolute',
                                top: '16px',
                                right: '16px',
                                background: 'none',
                                border: 'none',
                                color: isLightTheme ? '#94A3B8' : '#64748B',
                                cursor: 'pointer',
                                padding: '4px',
                                borderRadius: '50%'
                            }}
                        >
                            <X size={20} />
                        </button>

                        {/* Banana Mascot Icon & Header */}
                        <div style={{ textAlign: 'center', marginBottom: '1.25rem' }}>
                            <div style={{
                                width: '56px',
                                height: '56px',
                                borderRadius: '18px',
                                background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '2rem',
                                marginBottom: '0.5rem',
                                boxShadow: '0 8px 20px rgba(245, 158, 11, 0.35)',
                                border: '2px solid #FCD34D'
                            }}>
                                🍌
                            </div>
                            <h3 style={{ margin: '0 0 4px 0', fontSize: '1.35rem', fontWeight: 800, fontFamily: "'Outfit', sans-serif" }}>
                                Unlock Unlimited Gen AI
                            </h3>
                            <p style={{ margin: 0, fontSize: '0.84rem', color: isLightTheme ? '#475569' : '#94A3B8', lineHeight: 1.4 }}>
                                You have used your 5 free generations. Upgrade to keep generating text, posters, videos &amp; songs!
                            </p>
                        </div>

                        {/* Plan Selection Cards: AI+ Plan (₹1499) vs AI Unlimited (₹199) */}
                        <div className="ai-upgrade-modal-grid" style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '0.75rem',
                            marginBottom: '1.25rem'
                        }}>
                            {/* Card 1: AI+ Plan (Featured VIP) */}
                            <div
                                onClick={() => setModalSelectedPlan('ai_plus')}
                                style={{
                                    border: modalSelectedPlan === 'ai_plus' ? '2px solid #F59E0B' : (isLightTheme ? '1.5px solid #E2E8F0' : '1.5px solid rgba(255, 255, 255, 0.1)'),
                                    background: modalSelectedPlan === 'ai_plus' ? (isLightTheme ? '#FFFBEB' : 'rgba(245, 158, 11, 0.12)') : (isLightTheme ? '#F8FAFC' : 'rgba(255, 255, 255, 0.03)'),
                                    borderRadius: '16px',
                                    padding: '1rem',
                                    cursor: 'pointer',
                                    position: 'relative',
                                    transition: 'all 0.2s',
                                    boxShadow: modalSelectedPlan === 'ai_plus' ? '0 6px 20px rgba(245, 158, 11, 0.25)' : 'none'
                                }}
                            >
                                <span style={{
                                    position: 'absolute',
                                    top: '-10px',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                                    color: '#FFFFFF',
                                    fontSize: '0.58rem',
                                    fontWeight: 800,
                                    padding: '2px 8px',
                                    borderRadius: '10px',
                                    letterSpacing: '0.5px',
                                    whiteSpace: 'nowrap'
                                }}>
                                    👑 BEST VALUE VIP
                                </span>

                                <div style={{ fontSize: '0.74rem', fontWeight: 800, color: '#D97706', textTransform: 'uppercase', marginBottom: '4px' }}>
                                    AI+ ROYAL VIP
                                </div>
                                <div style={{ fontSize: '1.5rem', fontWeight: 900, color: isLightTheme ? '#0F172A' : '#F8FAFC', marginBottom: '4px' }}>
                                    ₹1499
                                </div>
                                <div style={{ fontSize: '0.72rem', color: isLightTheme ? '#64748B' : '#94A3B8', marginBottom: '8px' }}>
                                    All-In-One celebration VIP
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', fontSize: '0.74rem', textAlign: 'left' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <Check size={13} color="#10B981" strokeWidth={3} />
                                        <span><strong>Unlimited Nano Banana AI</strong></span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <Check size={13} color="#10B981" strokeWidth={3} />
                                        <span><strong>5,000 Guests</strong> WhatsApp Limit</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <Check size={13} color="#10B981" strokeWidth={3} />
                                        <span><strong>Photo, Video &amp; Song</strong> Sending</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <Check size={13} color="#10B981" strokeWidth={3} />
                                        <span><strong>24h &amp; 12h Reminders</strong> &amp; RSVP</span>
                                    </div>
                                </div>
                            </div>

                            {/* Card 2: AI Unlimited (₹199) */}
                            <div
                                onClick={() => setModalSelectedPlan('ai_unlimited')}
                                style={{
                                    border: modalSelectedPlan === 'ai_unlimited' ? '2px solid #F59E0B' : (isLightTheme ? '1.5px solid #E2E8F0' : '1.5px solid rgba(255, 255, 255, 0.1)'),
                                    background: modalSelectedPlan === 'ai_unlimited' ? (isLightTheme ? '#FFFBEB' : 'rgba(245, 158, 11, 0.12)') : (isLightTheme ? '#F8FAFC' : 'rgba(255, 255, 255, 0.03)'),
                                    borderRadius: '16px',
                                    padding: '1rem',
                                    cursor: 'pointer',
                                    position: 'relative',
                                    transition: 'all 0.2s',
                                    boxShadow: modalSelectedPlan === 'ai_unlimited' ? '0 6px 20px rgba(245, 158, 11, 0.25)' : 'none'
                                }}
                            >
                                <div style={{ fontSize: '0.74rem', fontWeight: 800, color: '#F59E0B', textTransform: 'uppercase', marginBottom: '4px' }}>
                                    🍌 AI UNLIMITED
                                </div>
                                <div style={{ fontSize: '1.5rem', fontWeight: 900, color: '#10B981', marginBottom: '4px' }}>
                                    ₹199
                                </div>
                                <div style={{ fontSize: '0.72rem', color: isLightTheme ? '#64748B' : '#94A3B8', marginBottom: '8px' }}>
                                    Standalone AI studio pass
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', fontSize: '0.74rem', textAlign: 'left' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <Check size={13} color="#10B981" strokeWidth={3} />
                                        <span><strong>Unlimited AI Text</strong></span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <Check size={13} color="#10B981" strokeWidth={3} />
                                        <span><strong>Ultra-HD Posters</strong> (1080px)</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <Check size={13} color="#10B981" strokeWidth={3} />
                                        <span><strong>Cinematic Video Teasers</strong></span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <Check size={13} color="#10B981" strokeWidth={3} />
                                        <span><strong>Celebratory Songs</strong> (WAV)</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Actions */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                            <button
                                type="button"
                                onClick={() => handleUpgradeToAiPlan(false)}
                                disabled={isUpgrading}
                                style={{
                                    width: '100%',
                                    padding: '13px 20px',
                                    borderRadius: '12px',
                                    border: 'none',
                                    background: modalSelectedPlan === 'ai_plus'
                                        ? 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)'
                                        : 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                                    color: '#FFFFFF',
                                    fontWeight: 800,
                                    fontSize: '0.98rem',
                                    cursor: isUpgrading ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    boxShadow: modalSelectedPlan === 'ai_plus'
                                        ? '0 4px 16px rgba(245, 158, 11, 0.4)'
                                        : '0 4px 16px rgba(16, 185, 129, 0.4)'
                                }}
                            >
                                <Zap size={18} />
                                {isUpgrading
                                    ? 'Connecting to Cashfree Gateway...'
                                    : (modalSelectedPlan === 'ai_plus' ? '👑 Upgrade to AI+ Royal VIP Plan (₹1499)' : '⚡ Purchase AI Unlimited Plan (₹199)')}
                            </button>

                            <button
                                type="button"
                                onClick={() => handleUpgradeToAiPlan(true)}
                                disabled={isUpgrading}
                                style={{
                                    width: '100%',
                                    padding: '9px 16px',
                                    borderRadius: '12px',
                                    border: isLightTheme ? '1.5px solid #CBD5E1' : '1px solid rgba(255, 255, 255, 0.12)',
                                    background: isLightTheme ? '#F1F5F9' : 'rgba(255, 255, 255, 0.05)',
                                    color: isLightTheme ? '#475569' : '#CBD5E1',
                                    fontWeight: 700,
                                    fontSize: '0.82rem',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '6px'
                                }}
                            >
                                🧪 Instant Test Simulator Activation (Free Dev/Test)
                            </button>

                            <button
                                type="button"
                                onClick={() => setIsUpgradeModalOpen(false)}
                                style={{
                                    width: '100%',
                                    padding: '6px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    background: 'none',
                                    color: isLightTheme ? '#94A3B8' : '#64748B',
                                    fontSize: '0.8rem',
                                    cursor: 'pointer'
                                }}
                            >
                                Maybe Later (Continue with Classic Templates)
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
